import { config } from "@config/env";
import { handleError, AppError, ValidationError, AuthError, NotFoundError, NetworkError } from './errorHandler';
import { getColorByIndex, DEFAULT_COLOR, MAX_INDEX_COLORS } from './colorScale';
import { log, warn, debug, error as logError } from './console';
import {
	abandonUserSession,
	handleUnauthorizedWithResilientRefresh,
	isStreamApiUrl,
} from './resilientAuth401';

import * as arrow from 'apache-arrow';

/** Max length for any stringified value in error log context to avoid huge DB payloads. */
const ERROR_LOG_CONTEXT_MAX_LENGTH = 500;

/**
 * Build a safe summary of an error response for logging. Omits the full errorResponse body
 * so we never log huge payloads (e.g. accidental data-as-error) to the database.
 */
function buildErrorResponseLogSummary(
	errorResponse: { message?: string; error?: string; detail?: unknown; data?: Record<string, unknown> },
	status: number,
	serverErrorMessage: string | null,
	url?: string,
	method?: string
): Record<string, unknown> {
	const errorLines = errorResponse.data?.error_lines;
	const outputLines = errorResponse.data?.output_lines;
	return {
		status,
		hasData: !!errorResponse.data,
		hasDetail: !!errorResponse.detail,
		message: serverErrorMessage,
		...(url ? { url } : {}),
		...(method ? { method } : {}),
		errorLines: Array.isArray(errorLines)
			? errorLines.slice(-5).map((line: unknown) =>
				typeof line === 'string' && line.length > 300 ? line.substring(0, 300) + '...' : line
			)
			: null,
		outputLines: Array.isArray(outputLines) ? outputLines.slice(-10) : null,
		returnCode: errorResponse.data?.return_code ?? null
	};
}

/**
 * Proxies (e.g. nginx) often return HTML for 502/504 while the app expects JSON.
 * Produce a short log line and a stable user-facing message instead of raw HTML.
 */
function summarizeNonJsonErrorResponse(
	status: number,
	contentType: string | null,
	plainBody: string
): { logMessage: string; userMessage: string } {
	const trimmed = plainBody.trim();
	const titleMatch = trimmed.match(/<title>\s*([^<]+?)\s*<\/title>/i);
	const title = titleMatch?.[1]?.replace(/\s+/g, ' ').trim();
	const isHtml =
		Boolean(contentType?.includes('text/html')) ||
		/<html[\s>]/i.test(trimmed) ||
		/<!doctype\s+html/i.test(trimmed);

	if (status === 504 || (isHtml && /gateway\s*time-?out/i.test(title || ''))) {
		return {
			logMessage: `[global.ts] HTTP ${status} non-JSON (${contentType || 'unknown'}): gateway time-out (proxy limit may be exceeded)`,
			userMessage:
				'Gateway time-out: the request took longer than the infrastructure allows. Try again, split the workload, or raise proxy read timeouts for long script runs.',
		};
	}
	if (status === 502 || (isHtml && /bad\s+gateway/i.test(title || ''))) {
		return {
			logMessage: `[global.ts] HTTP ${status} non-JSON (${contentType || 'unknown'}): bad gateway`,
			userMessage: 'Bad gateway: an upstream server did not respond. Please try again shortly.',
		};
	}
	if (status === 503 || (isHtml && /service\s*unavailable/i.test(title || ''))) {
		return {
			logMessage: `[global.ts] HTTP ${status} non-JSON (${contentType || 'unknown'}): service unavailable`,
			userMessage: 'Service temporarily unavailable. Please try again shortly.',
		};
	}
	if (isHtml && title) {
		return {
			logMessage: `[global.ts] HTTP ${status} non-JSON (${contentType || 'unknown'}): ${title}`,
			userMessage: title.length > 280 ? `${title.slice(0, 280)}…` : title,
		};
	}
	const preview = trimmed.slice(0, 200);
	return {
		logMessage: `[global.ts] HTTP ${status} non-JSON (${contentType || 'unknown'})${preview ? `: ${preview}${trimmed.length > 200 ? '…' : ''}` : ''}`,
		userMessage: trimmed.slice(0, 500) || `HTTP ${status}`,
	};
}

const STREAM_API_PATH = '/api/stream/';
/** Stream HTTP statuses that must not trigger global session logout (service-local failures). */
const STREAM_HTTP_SOFT_FAIL = new Set([401, 403, 500, 502, 503, 504]);

function extractStreamErrorHint(bodyText: string, contentType: string | null): string {
	if (!bodyText?.trim()) {
		return '';
	}
	try {
		if (contentType?.includes('application/json')) {
			const parsed = JSON.parse(bodyText) as {
				message?: string;
				error?: string;
				detail?: string;
				data?: { error?: string };
			};
			if (typeof parsed.message === 'string' && parsed.message) {
				return parsed.message;
			}
			if (typeof parsed.error === 'string' && parsed.error) {
				return parsed.error;
			}
			if (typeof parsed.detail === 'string' && parsed.detail) {
				return parsed.detail;
			}
			if (parsed.data && typeof parsed.data.error === 'string') {
				return parsed.data.error;
			}
		}
	} catch {
		/* ignore parse errors */
	}
	return bodyText.slice(0, 280);
}

/**
 * Stream service errors are best-effort and must not clear the user's app session.
 * A 401 from server_stream often means stream-local auth/config — not that the user is logged out.
 */
async function tryStreamSoftFailResponse(
	fullUrl: string,
	response: Response,
	logPrefix: string,
	hint?: string | null
): Promise<ApiResponse | null> {
	if (!isStreamApiUrl(fullUrl) || !STREAM_HTTP_SOFT_FAIL.has(response.status)) {
		return null;
	}

	let message = hint?.trim() || '';
	if (!message) {
		try {
			const contentType = response.headers.get('content-type');
			const bodyText = await response.text();
			message = extractStreamErrorHint(bodyText, contentType);
		} catch {
			/* ignore body read errors */
		}
	}

	const { debug: streamDbg } = await import('./console');
	const authStatuses = response.status === 401 || response.status === 403;
	const serviceHint = authStatuses
		? ' (stream service rejected auth — confirm server_stream is running and JWT_SECRET matches server_app)'
		: ' (ensure server_stream is running; Vite proxies /api/stream to VITE_STREAM_PORT, default 8099)';
	streamDbg(
		`[${logPrefix}] Stream HTTP ${response.status} for ${fullUrl}${message ? ` — ${message}` : ''}${serviceHint}`
	);

	const type = authStatuses ? 'StreamAuthError' : 'StreamUnavailable';
	const fallback = `Stream service HTTP ${response.status}`;
	return {
		success: false,
		error: message || fallback,
		message: message || fallback,
		status: response.status,
		type,
	};
}

function parseBinary(buffer: ArrayBuffer): any[] {
    const table = arrow.tableFromIPC(buffer);
    return table.toArray();
}

export function setCookie(cname: string, cvalue: string, exdays: number): void {
	try {
		const d = new Date();
		d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
		let expires = "expires=" + d.toUTCString();
		document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
	} catch (err) {
		// Silent fail
	}
}
  
export function getCookie(cname: string): string {
	try {
		let name = cname + "=";
		let ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') {
				c = c.substring(1);
			}
			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length);
			}
		}
		return "";
	} catch (err) {
		return "";
	}
}

export function deleteCookie(cname: string): void {
	try {
		document.cookie = cname + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
	} catch (err) {
		// Silent fail
	}
}

/**
 * Warm up the CSRF double-submit cookie with a safe GET to /api/health.
 * Call before anonymous POSTs (register, verify, forgot password, reset password, etc.)
 * when the session may not have hit the API yet (deep links, new tab, cleared cookies).
 */
export async function ensureCsrfCookie(signal?: AbortSignal): Promise<void> {
	try {
		await fetch(`${window.location.origin}/api/health`, {
			method: 'GET',
			credentials: 'include',
			signal,
		});
	} catch {
		/* ignore; caller POST should fail clearly if the cookie was never set */
	}
}

export const round = (value: number | string, decimals: number): number => {
	try {
		let val = Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);

		if (isNaN(val)) {
			return 0;
		}

		return val;
	} catch {
		return 0;
	}
}

export const removeLastChar = (input: string, char: string): string => {
    if (input.endsWith(char)) {
        return input.slice(0, -1);
    }

    return input;
}

/**
 * Clean quotes from a string value
 * Removes leading and trailing double quotes (") and single quotes (')
 * This is useful when loading values from persistent storage or sessionStorage
 * that may have been incorrectly serialized with quotes
 */
export const cleanQuotes = (value: string | null | undefined): string => {
    if (!value || typeof value !== 'string') {
        return value || '';
    }
    // Remove leading and trailing quotes (both single and double)
    return value.replace(/^["']|["']$/g, '').trim();
}

// Lazy-loaded d3 format functions for myTickFormat
let formatCache: {
	".1f": (d: number) => string;
	".2f": (d: number) => string;
	".3f": (d: number) => string;
	".4f": (d: number) => string;
	".5f": (d: number) => string;
} | null = null;

// Pre-load d3 format functions in the background
import('d3').then(d3 => {
	formatCache = {
		".1f": d3.format(".1f"),
		".2f": d3.format(".2f"),
		".3f": d3.format(".3f"),
		".4f": d3.format(".4f"),
		".5f": d3.format(".5f")
	};
}).catch(() => {
	// Fallback if d3 fails to load
	formatCache = {
		".1f": (d: number) => d.toFixed(1),
		".2f": (d: number) => d.toFixed(2),
		".3f": (d: number) => d.toFixed(3),
		".4f": (d: number) => d.toFixed(4),
		".5f": (d: number) => d.toFixed(5)
	};
});

export const myTickFormat = function (d: number): string {
	let limits = [1000000000000000, 1000000000000, 1000000000, 1000000, 1000];
	let shorteners = ['Q', 'T', 'B', 'M', 'K'];
	let sign = d < 0 ? -1 : 1;
	let absD = Math.abs(d);
	
	for (let i in limits) {
		if (absD >= limits[i]) {
			return (sign * (absD / limits[i])).toFixed() + shorteners[i];
		}
	}

	// Use cached format functions if available, otherwise use fallback
	if (formatCache) {
		if (absD <= 0.000001) {
			return formatCache[".1f"](d);
		} else if (absD <= 0.00001) {
			return formatCache[".5f"](d);
		} else if (absD <= 0.0001) {
			return formatCache[".4f"](d);
		} else if (absD <= 0.001) {
			return formatCache[".3f"](d);
		} else if (absD <= 0.01) {
			return formatCache[".2f"](d);
		} else {
			return formatCache[".1f"](d);
		}
	} else {
		// Fallback until d3 loads
		if (absD <= 0.000001) {
			return d.toFixed(1);
		} else if (absD <= 0.00001) {
			return d.toFixed(5);
		} else if (absD <= 0.0001) {
			return d.toFixed(4);
		} else if (absD <= 0.001) {
			return d.toFixed(3);
		} else if (absD <= 0.01) {
			return d.toFixed(2);
		} else {
			return d.toFixed(1);
		}
	}
};

export function itemExists(list: any[], value: any): boolean {
    return list.some(item => item === value);
}

export function addItem(list: any[], value: any): any[] {
    if (typeof value === "number" && value > 0 && !list.some(item => item === value)) {
        return [...list, value]; 
    }
    return list;
}

export function replaceItem(list: any[], value: any): any[] {
    let filteredList = list.filter(item => item !== value);
    if (!list.some(item => item === value)) {
        filteredList.push(value);
    }
    return filteredList;
}

export function removeItem(list: any[], value: any): any[] {
    return list.filter(item => item !== value);
}

// Safe toNodeList helper which works in browser contexts and no-ops in Node/test environments.
// The previous implementation referenced document at module load, which crashed in non-DOM
// environments (e.g. server-side logging or tests). We now guard against that.
export const toNodeList = (function () {
  if (typeof document === 'undefined') {
    // In non-browser environments, just normalize to an empty NodeList-like object.
    // Callers in those environments should not rely on actual DOM behavior.
    return function (_nodeArray: any): NodeList {
      return {
        length: 0,
        item: () => null,
        [Symbol.iterator]: function* () { /* empty */ },
      } as unknown as NodeList;
    };
  }

  const emptyNL = document.createDocumentFragment().childNodes;

  return function (nodeArray: any): NodeList {
    if (nodeArray instanceof NodeList) return nodeArray;

    if (!Array.isArray(nodeArray)) nodeArray = [nodeArray];

    const mockNL = Object.create(emptyNL, {
      length: {
        value: nodeArray.length,
        enumerable: false,
      },
      item: {
        value: function (i: number) {
          return (this as any)[+i || 0];
        },
        enumerable: false,
      },
    });

    nodeArray.forEach((v: any, i: number) => (mockNL as any)[i] = v);

    return mockNL as NodeList;
  };
})();

export function groupBy(collection: any[], key?: string | number): any[] {
	let result: any[] = [];
	if (key !== undefined) {
		let keyvalues = collection.map(d => ({
			val: d[key]
		}));
	
		keyvalues.forEach(d => {
			let val = d.val;
	
			let exists = false;
			result.forEach(function(r) {
				if (r === val) {
					exists = true;
				}
			});
	
			if (exists === false) {
				if (val !== undefined) {
					result.push(val);
				}
			}
		});
	} else {
		collection.forEach(val => {
			let exists = false;
			result.forEach(function(r) {
				if (r === val) {
					exists = true;
				}
			});
	
			if (exists === false) {
				if (val !== undefined) {
					result.push(val);
				}
			}
		});
	}

    return result.sort();
}

/**
 * Format datetime with optional timezone
 * @param input - Date input (string, Date, or number)
 * @param timezone - Optional timezone string (e.g., 'Europe/Madrid', 'UTC'). If not provided, uses browser local timezone.
 * @returns Formatted datetime string or undefined
 */
export function formatDateTime(input: any, timezone?: string | null): string | undefined {
	if (input === undefined || input === null) {
		return undefined;
	}

	try {
		let date: Date;
		if (input instanceof Date) {
			date = input;
		} else {
			const date_str = input.toString();
			if (date_str.indexOf("Z") === 0) {
				date = new Date(date_str + "Z");
			} else {
				date = new Date(date_str);
			}
		}

		if (isNaN(date.getTime())) {
			return undefined;
		}

		// If timezone is provided, use Intl.DateTimeFormat
		if (timezone) {
			try {
				const formatter = new Intl.DateTimeFormat('en-US', {
					timeZone: timezone,
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				});
				const parts = formatter.formatToParts(date);
				const year = parts.find(p => p.type === 'year')?.value || '';
				const month = parts.find(p => p.type === 'month')?.value || '';
				const day = parts.find(p => p.type === 'day')?.value || '';
				const hour = parts.find(p => p.type === 'hour')?.value || '';
				const minute = parts.find(p => p.type === 'minute')?.value || '';
				const second = parts.find(p => p.type === 'second')?.value || '';
				return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
			} catch (tzError) {
				// Fall back to browser timezone if timezone is invalid
				warn('Invalid timezone:', timezone, tzError);
			}
		}

		// Fallback to original behavior (browser local timezone)
		function twoDigits(num: number): string {
			return ('0' + num).slice(-2);
		}

		return date.getFullYear() + "-" +
			twoDigits(date.getMonth() + 1) + "-" +
			twoDigits(date.getDate()) + " " +
			twoDigits(date.getHours()) + ":" +
			twoDigits(date.getMinutes()) + ":" +
			twoDigits(date.getSeconds());
	} catch (error) {
		return undefined;
	}
}

/**
 * Format date with optional timezone
 * @param input - Date input (string, Date, or number)
 * @param timezone - Optional timezone string (e.g., 'Europe/Madrid', 'UTC'). If not provided, uses browser local timezone.
 * @returns Formatted date string or undefined
 */
export function formatDate(input: any, timezone?: string | null): string | undefined {
	if (input === undefined || input === null) {
		return undefined;
	}

	try {
		let date: Date;
		if (input instanceof Date) {
			date = input;
		} else {
			const date_str = input.toString();
			if (date_str.indexOf("Z") === 0) {
				date = new Date(date_str + "Z");
			} else {
				date = new Date(date_str);
			}
		}

		if (isNaN(date.getTime())) {
			return undefined;
		}

		// If timezone is provided, use Intl.DateTimeFormat
		if (timezone) {
			try {
				const formatter = new Intl.DateTimeFormat('en-US', {
					timeZone: timezone,
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				});
				const parts = formatter.formatToParts(date);
				const year = parts.find(p => p.type === 'year')?.value || '';
				const month = parts.find(p => p.type === 'month')?.value || '';
				const day = parts.find(p => p.type === 'day')?.value || '';
				return `${year}-${month}-${day}`;
			} catch (tzError) {
				// Fall back to browser timezone if timezone is invalid
				warn('Invalid timezone:', timezone, tzError);
			}
		}

		// Fallback to original behavior (browser local timezone)
		function twoDigits(num: number): string {
			return ('0' + num).slice(-2);
		}

		return date.getFullYear() + "-" +
			twoDigits(date.getMonth() + 1) + "-" +
			twoDigits(date.getDate());
	} catch (error) {
		return undefined;
	}
}

/**
 * Return UTC milliseconds for the start and end of a calendar day in a given timezone.
 * Used for querying events/races by "day" in the dataset's local timezone.
 * @param dateStr - YYYY-MM-DD
 * @param timezone - IANA timezone (e.g. 'Australia/Sydney'). Falls back to UTC if invalid.
 * @returns { startMs, endMs } for start of day 00:00:00.000 and end of day 23:59:59.999 in that timezone
 */
export function getDayBoundsInTimezone(dateStr: string, timezone: string | null): { startMs: number; endMs: number } {
	const dayUtcStart = new Date(dateStr + 'T00:00:00.000Z').getTime();
	const dayUtcEnd = new Date(dateStr + 'T23:59:59.999Z').getTime();
	// Range that could cover this local day in any timezone (±26h to handle DST)
	const searchStart = dayUtcStart - 26 * 60 * 60 * 1000;
	const searchEnd = dayUtcEnd + 26 * 60 * 60 * 1000;
	const step = 60 * 1000; // 1 minute
	let startMs = dayUtcStart;
	let endMs = dayUtcEnd;
	if (timezone) {
		try {
			for (let ts = searchStart; ts <= searchEnd; ts += step) {
				const formatted = formatDate(new Date(ts), timezone);
				if (formatted === dateStr) {
					startMs = ts;
					break;
				}
			}
			for (let ts = searchEnd; ts >= searchStart; ts -= step) {
				const formatted = formatDate(new Date(ts), timezone);
				if (formatted === dateStr) {
					endMs = Math.min(ts + 59 * 1000 + 999, searchEnd);
					break;
				}
			}
		} catch {
			// fall through to UTC day
		}
	}
	return { startMs, endMs };
}

/**
 * Convert a local date + time in a given timezone to a UTC Date.
 * Used when the user edits event times in dataset local time; we need the UTC instant for the API.
 * @param dateStr - YYYY-MM-DD
 * @param timeStr - HH:mm or HH:mm:ss
 * @param timezone - IANA timezone (e.g. 'Europe/Madrid'). If null, parses as browser local.
 * @returns UTC Date or null if not found/invalid
 */
export function localTimeInTimezoneToUtcDate(
	dateStr: string,
	timeStr: string,
	timezone: string | null
): Date | null {
	const normalized = timeStr.trim();
	const parts = normalized.split(':');
	let timeNormalized = normalized;
	if (parts.length === 2) {
		timeNormalized = `${parts[0]}:${parts[1]}:00`;
	} else if (parts.length === 1) {
		timeNormalized = `${parts[0]}:00:00`;
	}

	if (!timezone || timezone.trim() === '') {
		try {
			const d = new Date(dateStr + 'T' + timeNormalized);
			return isNaN(d.getTime()) ? null : d;
		} catch {
			return null;
		}
	}

	const target = `${dateStr} ${timeNormalized}`.trim();
	const { startMs, endMs } = getDayBoundsInTimezone(dateStr, timezone);
	const step = 60 * 1000;
	let lo = startMs;
	let hi = endMs;
	while (lo <= hi) {
		const mid = Math.floor((lo + hi) / (2 * step)) * step;
		const formatted = formatDateTime(new Date(mid), timezone);
		if (formatted === target) return new Date(mid);
		if (!formatted || formatted < target) {
			lo = mid + step;
		} else {
			hi = mid - step;
		}
	}
	return null;
}

/**
 * Fetch timezone for a given class/project/date from the date/timezone API.
 * Used so day-based APIs (date/dataset_id, date/races) and day-range queries use local date.
 */
export async function getTimezoneForDate(
	className: string,
	projectId: number,
	date: string
): Promise<string | null> {
	const dateNorm = String(date).replace(/[-/]/g, '');
	const dateDisplay = dateNorm.length === 8
		? `${dateNorm.slice(0, 4)}-${dateNorm.slice(4, 6)}-${dateNorm.slice(6, 8)}`
		: String(date);
	try {
		const { apiEndpoints } = await import('../config/env.js');
		const url = `${apiEndpoints.app.datasets}/date/timezone?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(dateDisplay)}`;
		const resp = await getData(url);
		const data = (resp && (resp as any).data) ?? resp;
		if (data?.timezone) return String(data.timezone).trim();
	} catch {
		// ignore
	}
	return null;
}

export interface MediaTimezoneWindow {
	start?: Date | string | null;
	end?: Date | string | null;
	timezone?: string | null;
}

function trimTimezone(timezone: unknown): string | null {
	if (timezone == null) return null;
	const s = String(timezone).trim();
	return s !== '' ? s : null;
}

/**
 * Timezone for video-sync timeline labels and known-time input.
 * Matches VideoSyncHelper: selected window → first media window → dataset/date fallback.
 */
export function resolveMediaDisplayTimezone(options: {
	windows?: MediaTimezoneWindow[] | null;
	selectedWindows?: MediaTimezoneWindow[] | null;
	fallbackTimezone?: string | null;
}): string | null {
	const selectedWindows = options.selectedWindows ?? [];
	const windows = options.windows ?? [];
	const fallback = trimTimezone(options.fallbackTimezone);

	if (selectedWindows.length > 0) {
		const tz = trimTimezone(selectedWindows[0]?.timezone);
		if (tz) return tz;
	}
	for (const w of windows) {
		const tz = trimTimezone(w?.timezone);
		if (tz) return tz;
	}
	return fallback;
}

/**
 * Format time with optional timezone
 * @param input - Date input (string, Date, or number)
 * @param timezone - Optional timezone string (e.g., 'Europe/Madrid', 'UTC'). If not provided, uses browser local timezone.
 * @returns Formatted time string or undefined
 */
export function formatTime(input: any, timezone?: string | null): string | undefined {
	if (input === undefined || input === null) {
		return undefined;
	}

	try {
		let date: Date;
		if (input instanceof Date) {
			date = input;
		} else {
			const date_str = input.toString();
			if (date_str.indexOf("Z") === 0) {
				date = new Date(date_str + "Z");
			} else {
				date = new Date(date_str);
			}
		}

		if (isNaN(date.getTime())) {
			return undefined;
		}

		// If timezone is provided, use Intl.DateTimeFormat
		if (timezone) {
			try {
				const formatter = new Intl.DateTimeFormat('en-US', {
					timeZone: timezone,
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false
				});
				const parts = formatter.formatToParts(date);
				const hour = parts.find(p => p.type === 'hour')?.value || '';
				const minute = parts.find(p => p.type === 'minute')?.value || '';
				const second = parts.find(p => p.type === 'second')?.value || '';
				const result = `${hour}:${minute}:${second}`;
				// Debug first few conversions to verify timezone is working
				if (Math.random() < 0.001) {
					debug(`[formatTime] UTC: ${date.toISOString()}, TZ: ${timezone}, Result: ${result}`);
				}
				return result;
			} catch (tzError) {
				// Fall back to browser timezone if timezone is invalid
				warn('Invalid timezone:', timezone, tzError);
			}
		}

		// Fallback to original behavior (browser local timezone)
		function twoDigits(num: number): string {
			return ('0' + num).slice(-2);
		}
	
		return twoDigits(date.getHours()) + ":" +
			twoDigits(date.getMinutes()) + ":" +
			twoDigits(date.getSeconds());
	} catch (error) {
		return undefined;
	}
}

export const formatSeconds = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

export const getIndexColor = (list: any[], id: any): string | undefined => {
	if (list.length > 0) {
		const index = list.findIndex(item => item === id);
	
		if (index > -1) {
			// If more than MAX_INDEX_COLORS events are selected, all selected events should be blue
			if (list.length > MAX_INDEX_COLORS) {
				return DEFAULT_COLOR; // Blue for all when more than 8 selected
			}
			// Use global color scale for consistent colors across all components
			return getColorByIndex(index);
		} else {
			return 'lightgray';
		}
	} else {
		return 'lightgray';
	}
}

export const checkSelection = (list: any[], id: any): boolean => {
	if (list.length > 0) {
		const index = list.findIndex(item => item === id);
	
		if (index > -1) {
			return true;
		} else {
			return false;
		}
	} else {
		return false;
	}
}

// Generate a unique ID for charts
export function generateUniqueId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function AngleSubtract180(first: number | string, second: number | string): number {
    try {
        first = parseFloat(first.toString());
        second = parseFloat(second.toString());

        if (first > 720 || second > 720 || first < -720 || second < -720) {
            return 0;
        }

        let subtract = first - second;

		while (subtract > 180) {
			subtract -= 360;
		}

		while (subtract < -180) {
			subtract += 360;
		}

        return subtract;
    } catch (error) {
        return 0;
    }
}

export function AngleSubtract(first: number | string, second: number | string): number {
    let result = 0;
    try {
        first = parseFloat(first.toString());
        second = parseFloat(second.toString());
        
        let subtract = first - second;
        
        result = (subtract + 180) % 360 - 180;
        
        if (result === -180) {
            return 180;
        } else {
            return result;
        }
    } catch (error) {
        return 0;
    }
}

export function AngleBetween(first: number | string, second: number | string): number {
    try {
        first = parseFloat(first.toString());
        second = parseFloat(second.toString());

        if (first >= 0 || second >= 0 || first <= 0 || second <= 0) {
            if (first > 720 || second > 720 || first < -720 || second < -720) {
                return 0;
            }

            let between = first - second;

            while (between > 180) {
                between -= 360;
            }

            while (between < -180) {
                between += 360;
            }

            return Math.abs(between);
        } else {
            if (first < 0) {
                first = Math.abs(first) + 180;
            } else {
                second = Math.abs(second) + 180;
            }

            if (first > 720 || second > 720 || first < -720 || second < -720) {
                return 0;
            }

            let between = first - second;

            while (between > 180) {
                between -= 360;
            }

            while (between < -180) {
                between += 360;
            }

            return Math.abs(between);
        }
    } catch (error) {
        return 0;
    }
}

interface ApiResponse {
    success: boolean;
    data?: any;
    error?: string;
    status?: number;
    message?: string;
    type?: string;
    /**
     * postBinary only: a 2xx that carried no body. NOT the same as a 204 — the server says it had rows to
     * send, so an empty body means the read failed (e.g. a parquet caught mid-rewrite) and is worth a retry.
     * A genuine 204 is `status === 204` and must not be retried.
     */
    emptyBody?: boolean;
    /** Parsed `Retry-After` (seconds) when the server shed load (429/503), so callers can pace retries. */
    retryAfterSeconds?: number;
}

/** Minimal getData/postData result shape for cancellation checks. */
export type GetDataResultLike = Pick<ApiResponse, 'success' | 'error' | 'message' | 'status' | 'type'>;

/** User/navigation abort — do not treat as an application error in logs. */
export function isGetDataCancelled(result: GetDataResultLike): boolean {
    return (
        result.type === 'AbortError' ||
        result.error === 'Request cancelled' ||
        result.message === 'Request cancelled'
    );
}

/** Per-call behaviour for getData. */
export interface GetDataOptions {
	/**
	 * The caller survives a failed request, so a network failure is logged as a trace rather than as an
	 * application error. For background polls whose result only refreshes a passive indicator — see
	 * AuthRequestOptions.bestEffort in authManager.ts.
	 */
	bestEffort?: boolean;
}

export async function getData(url: string, signal?: AbortSignal, options: GetDataOptions = {}): Promise<ApiResponse> {
	try {
		// Import authManager dynamically to avoid circular dependencies
		const { authManager } = await import('./authManager');
		
		// Construct full URL
		// If URL is already absolute (starts with http), use as-is
		// If URL starts with /api, use as-is (already a relative URL for nginx)
		// Otherwise, prepend API_BASE_URL
		let fullUrl: string;
		if (url.startsWith('http')) {
			fullUrl = url;
		} else if (url.startsWith('/api')) {
			// URL already starts with /api, use as-is
			fullUrl = url;
		} else {
			// Prepend API_BASE_URL for relative URLs
			fullUrl = config.API_BASE_URL + url;
		}
		
		const quietGetData = !isStreamApiUrl(fullUrl);
		if (quietGetData) {
			log(`[getData] Making GET request to: ${fullUrl}`);
		}

		const getHeaders = {
			"Content-Type": "application/json",
			"Cache-Control": "no-cache",
			"Pragma": "no-cache",
		};

		let response = await authManager.makeAuthenticatedRequest(fullUrl, {
			method: "GET",
			cache: "no-store",
			headers: getHeaders,
			signal: signal
		}, { bestEffort: options.bestEffort });

		if (response.status === 304) {
			const cacheBustUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}_=${Date.now()}`;
			log(`[getData] 304 Not Modified for ${fullUrl}; retrying with cache bust`);
			response = await authManager.makeAuthenticatedRequest(cacheBustUrl, {
				method: "GET",
				cache: "no-store",
				headers: getHeaders,
				signal,
			}, { bestEffort: options.bestEffort });
		}
		
		if (quietGetData) {
			log(`[getData] Response status: ${response.status} ${response.statusText} for ${fullUrl}`);
		}

		if (!response.ok) {
			const streamSoftFailEarly = await tryStreamSoftFailResponse(fullUrl, response, 'getData');
			if (streamSoftFailEarly) {
				return streamSoftFailEarly;
			}
		}

		// Let backend sendResponse handle logging with proper source information
		if (response.ok) {
			// Handle 204 No Content responses (empty body)
			if (response.status === 204) {
				log(`[getData] 204 No Content for ${fullUrl}`);
				return { success: true, data: null, message: 'No content', status: response.status };
			}
			
			// Check if response has content before parsing JSON
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('application/json')) {
				// Non-JSON response or empty
				logError(`[getData] Invalid content-type: ${contentType} for ${fullUrl}`);
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, contentType });
			}
			
			// Check if response body is empty
			const text = await response.text();
			if (!text || text.trim() === '') {
				logError(`[getData] Empty response body for ${fullUrl}`);
				const error = new AppError('Empty response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status });
			}
			
			try {
				const response_json: ApiResponse = JSON.parse(text);
				if (!response_json.success) {
					logError(`[getData] API returned error for ${fullUrl}:`, response_json);
				}
				return response_json;
			} catch (jsonError) {
				logError(`[getData] JSON parse error for ${fullUrl}:`, jsonError);
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, jsonError: (jsonError as Error).message });
			}
		} else {
			// Check if user is not authenticated - suppress console errors for 401 but still log to server
			const { isLoggedIn } = await import('../store/userStore');
			const { debug: logDebug } = await import('./console');
			const userIsNotAuthenticated = !isLoggedIn();
			const shouldSuppress401 = userIsNotAuthenticated && response.status === 401;
			
			if (shouldSuppress401) {
				// User is not authenticated - log to server (database) but don't show in console
				logDebug(`[getData] HTTP 401 Unauthorized for ${fullUrl} (user not authenticated)`);
			} else {
				// User is authenticated or non-401 error - show in console
				logError(`[getData] HTTP error ${response.status} ${response.statusText} for ${fullUrl}`);
			}
			
			// Handle specific HTTP errors
			let error: AppError;
			let errorMessage: string;
			switch (response.status) {
				case 401: {
					try {
						const { authManager } = await import('./authManager');
						const authResult = await handleUnauthorizedWithResilientRefresh({
							fullUrl,
							retryRequest: () =>
								authManager.makeAuthenticatedRequest(fullUrl, {
									method: 'GET',
									cache: 'no-store',
									headers: {
										'Content-Type': 'application/json',
										'Cache-Control': 'no-cache',
										'Pragma': 'no-cache',
									},
									signal,
								}),
						});

						if (authResult.recovered && authResult.response) {
							if (authResult.response.status === 204) {
								return { success: true, data: null, message: 'No content', status: authResult.response.status };
							}

							const contentType = authResult.response.headers.get('content-type');
							if (!contentType || !contentType.includes('application/json')) {
								const err = new AppError('Invalid JSON response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
									contentType,
								});
							}

							const text = await authResult.response.text();
							if (!text || text.trim() === '') {
								const err = new AppError('Empty response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
								});
							}

							try {
								const response_json: ApiResponse = JSON.parse(text);
								return response_json;
							} catch (jsonError) {
								const err = new AppError('Invalid JSON response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
									jsonError: (jsonError as Error).message,
								});
							}
						}

						if (authResult.abandonSession) {
							await abandonUserSession(fullUrl);
						}
					} catch (redirectErr) {
						logError('Failed to redirect to login:', redirectErr);
					}

					error = new AuthError('Authentication required');
					errorMessage = 'Authentication required';
					break;
				}
				case 404:
					error = new NotFoundError('API endpoint');
					errorMessage = 'API endpoint not found';
					break;
				case 422:
					error = new ValidationError('Invalid request data');
					errorMessage = 'Invalid request data';
					break;
				case 500:
					error = new AppError('Server error', 500);
					errorMessage = 'Server error';
					break;
				default:
					error = new AppError(`HTTP ${response.status}: ${response.statusText}`, response.status);
					errorMessage = `HTTP ${response.status}: ${response.statusText}`;
			}
			
			return handleError(error, 'global.ts', { url: fullUrl, status: response.status });
		}
	} catch (error) {
		if ((error as Error).name === 'AbortError') {
			// Keep `message` populated too: callers that only read `message` would otherwise
			// fall back to their own generic failure text and surface a cancellation as an error.
			return { success: false, error: 'Request cancelled', message: 'Request cancelled', status: 0, type: 'AbortError' };
		}

		const message = (error as Error).message || 'Unknown network error';

		// Streaming endpoints and best-effort callers both fail gracefully: it is normal for them to be
		// unavailable, and the caller already handles the failed response. Logging is a trace, and the
		// response is built here rather than by handleError, which logs a second time at error level.
		const failsGracefully = url.includes('/api/stream/') || options.bestEffort === true;

		if (failsGracefully) {
			debug(`[getData] Network error for ${url} (best-effort):`, error);
			return {
				success: false,
				error: `Network error: ${message}`,
				status: 0,
				type: 'NetworkError'
			};
		}

		logError(`[getData] Network error for ${url}:`, error);

		const networkError = new NetworkError(`Network error: ${message}`);
		return handleError(networkError, 'global.ts', { url, signal: !!signal });
	}
}

export async function postBinary(url: string, body_json: any, signal?: AbortSignal): Promise<ApiResponse> {
	try {
		// Import authManager dynamically to avoid circular dependencies
		const { authManager } = await import('./authManager');
		
		// Construct full URL
		// If URL is already absolute (starts with http), use as-is
		// If URL starts with /api, use as-is (already a relative URL for nginx)
		// Otherwise, prepend API_BASE_URL
		let fullUrl: string;
		if (url.startsWith('http')) {
			fullUrl = url;
		} else if (url.startsWith('/api')) {
			// URL already starts with /api, use as-is
			fullUrl = url;
		} else {
			// Prepend API_BASE_URL for relative URLs
			fullUrl = config.API_BASE_URL + url;
		}

		const response = await authManager.makeAuthenticatedRequest(fullUrl, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body_json),
			signal: signal
		});

		// Log the HTTP request after getting the response
		if (response.ok) {
			// Handle 204 No Content (valid response when no data available)
			if (response.status === 204) {
				return { "success": true, "status": 204, "message": "No content", data: [] };
			}
			
			const buffer = await response.arrayBuffer();
			
			// Check if buffer is empty. Flagged so callers can retry this (unlike a 204): a 200 with no body
			// means the server intended to send rows and the read produced nothing.
			if (buffer.byteLength === 0) {
				return { "success": true, "status": response.status, "message": "Empty response", emptyBody: true, data: [] };
			}
			
			try {
				const json_data = parseBinary(buffer);
				return { "success": true, data: json_data };
			} catch (parseError) {
				// Log parsing error for debugging
				logError(`[postBinary] Error parsing Arrow format response:`, parseError);
				return { 
					"success": false, 
					"status": response.status, 
					"message": `Failed to parse response: ${(parseError as Error).message}`,
					"error": (parseError as Error).message
				};
			}
		} else {
			// Try to get the actual error message from the server
			let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
			try {
				// Check if response has content before parsing JSON
				const contentType = response.headers.get('content-type');
				if (contentType && contentType.includes('application/json')) {
					const text = await response.text();
					if (text && text.trim() !== '') {
						const errorData = JSON.parse(text);
						if (errorData.message) {
							errorMessage = errorData.message;
						}
					}
				}
			} catch (e) {
				// If we can't parse JSON, use the status text
			}
			// Surface Retry-After so a caller retrying a shed request waits as long as the server asked.
			const retryAfterRaw = response.headers.get('retry-after');
			const retryAfterSeconds = retryAfterRaw != null ? Number(retryAfterRaw) : NaN;
			return {
				"success": false,
				"status": response.status,
				"message": errorMessage,
				...(Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? { retryAfterSeconds } : {}),
			};
		}
	} catch (error) {
		const errorMessage = `Network error: ${(error as Error).message}`;
		return {"success": false, "error": (error as Error).message};
	}
}

export async function postData(url: string, body_json: any, signal?: AbortSignal): Promise<ApiResponse> {
	// Construct full URL
	// If URL is already absolute (starts with http), use as-is
	// If URL starts with /api, use as-is (already a relative URL for nginx)
	// Otherwise, prepend API_BASE_URL
	let fullUrl: string;
	if (url.startsWith('http')) {
		fullUrl = url;
	} else if (url.startsWith('/api')) {
		// URL already starts with /api, use as-is
		fullUrl = url;
	} else {
		// Prepend API_BASE_URL for relative URLs
		fullUrl = config.API_BASE_URL + url;
	}
	
	try {
		// Import authManager dynamically to avoid circular dependencies
		const { authManager } = await import('./authManager');
		
		const response = await authManager.makeAuthenticatedRequest(fullUrl, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body_json),
			signal: signal
		});

		// Let backend sendResponse handle logging with proper source information
		if (response.ok) {
			// Check if response has content before parsing JSON
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('application/json')) {
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, contentType });
			}
			
			// Check if response body is empty
			const text = await response.text();
			if (!text || text.trim() === '') {
				const error = new AppError('Empty response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status });
			}
			
			try {
				const response_json: ApiResponse = JSON.parse(text);
				return response_json;
			} catch (jsonError) {
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, jsonError: (jsonError as Error).message });
			}
		} else {
			// Try to extract error message and full response body from response
			// Clone the response so we can read it without consuming it
			const responseClone = response.clone();
			let serverErrorMessage: string | null = null;
			let serverErrorBody: any = null;
			try {
				const contentType = response.headers.get('content-type');
				if (contentType && contentType.includes('application/json')) {
					const text = await responseClone.text();
					if (text && text.trim() !== '') {
						const errorResponse = JSON.parse(text);
						// Server returns { success: false, message: "...", data: {...} } format
						// OR FastAPI HTTPException format: { detail: "..." }
						serverErrorMessage = errorResponse.message || errorResponse.error || errorResponse.detail || null;
						
						// For script execution errors, try to extract more detailed error message from data.error_lines
						if (errorResponse.data?.error_lines && Array.isArray(errorResponse.data.error_lines) && errorResponse.data.error_lines.length > 0) {
							const lastError = errorResponse.data.error_lines[errorResponse.data.error_lines.length - 1];
							if (lastError && typeof lastError === 'string' && lastError.trim()) {
								// Use the last error line as the message if it's more specific than the generic message
								if (!serverErrorMessage || serverErrorMessage === 'Script execution failed' || serverErrorMessage === 'Server error') {
									serverErrorMessage = lastError.length > 300 ? lastError.substring(0, 300) + '...' : lastError;
								}
							}
						}
						
						// Capture the full error response body (includes data with error_lines, output_lines, etc.)
						serverErrorBody = errorResponse;
						
						// Special case: 409 Conflict "Process already running" is expected and handled by callers
						// Return it without logging as an error
						if (response.status === 409 && serverErrorBody?.data?.process_already_running) {
							return {
								success: false,
								status: 409,
								message: serverErrorMessage || 'Process already running',
								data: serverErrorBody.data,
								type: 'Conflict'
							};
						}
						
					// Log for debugging (skip for 409 "Process already running").
					// Use a summary only — never log full errorResponse to avoid huge DB payloads (e.g. data returned as error body).
					const errorSummary = buildErrorResponseLogSummary(errorResponse, response.status, serverErrorMessage, fullUrl, 'POST');
					const summaryStr = JSON.stringify(errorSummary);
					logError('[global.ts] Server error response captured:', summaryStr.length > ERROR_LOG_CONTEXT_MAX_LENGTH ? summaryStr.substring(0, ERROR_LOG_CONTEXT_MAX_LENGTH) + '...' : summaryStr);
					} else {
						logError('[global.ts] Server returned empty error response body');
					}
				} else {
					const plainBody = (await responseClone.text()).trim();
					// Vite (and some proxies) return text/plain or HTML when the upstream (e.g. server_stream) is down.
					if (fullUrl.includes('/api/stream/')) {
						const { debug: postStreamDbg } = await import('./console');
						postStreamDbg(
							`[postData] Stream HTTP ${response.status} non-JSON for ${fullUrl} — start server_stream and match VITE_STREAM_PORT. Preview: ${plainBody.slice(0, 280)}`
						);
						return {
							success: false,
							message:
								plainBody.slice(0, 500) ||
								`Stream service returned ${response.status} (${contentType || 'non-JSON'})`,
							status: response.status,
							type: 'StreamUnavailable',
						};
					}
					const { logMessage, userMessage } = summarizeNonJsonErrorResponse(
						response.status,
						contentType,
						plainBody
					);
					serverErrorMessage = userMessage;
					logError(logMessage);
				}
			} catch (parseError) {
				// If we can't parse the error, log it for debugging
				logError('[global.ts] Error parsing server error response:', parseError);
			}

			const streamSoftFail = await tryStreamSoftFailResponse(
				fullUrl,
				response,
				'postData',
				serverErrorMessage
			);
			if (streamSoftFail) {
				return streamSoftFail;
			}
			
			let error: AppError;
			let errorMessage: string;
			switch (response.status) {
				case 401: {
					try {
						const { authManager } = await import('./authManager');
						const authResult = await handleUnauthorizedWithResilientRefresh({
							fullUrl,
							retryRequest: () =>
								authManager.makeAuthenticatedRequest(fullUrl, {
									method: 'POST',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify(body_json),
									signal,
								}),
						});

						if (authResult.recovered && authResult.response) {
							const contentType = authResult.response.headers.get('content-type');
							if (!contentType || !contentType.includes('application/json')) {
								const err = new AppError('Invalid JSON response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
									contentType,
								});
							}

							const text = await authResult.response.text();
							if (!text || text.trim() === '') {
								const err = new AppError('Empty response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
								});
							}

							try {
								const response_json: ApiResponse = JSON.parse(text);
								return response_json;
							} catch (jsonError) {
								const err = new AppError('Invalid JSON response from server', 502);
								return handleError(err, 'global.ts', {
									url: fullUrl,
									status: authResult.response.status,
									jsonError: (jsonError as Error).message,
								});
							}
						}

						if (authResult.abandonSession) {
							await abandonUserSession(fullUrl);
						}
					} catch (redirectErr) {
						logError('Failed to redirect to login:', redirectErr);
					}

					error = new AuthError('Authentication required');
					errorMessage = 'Authentication required';
					break;
				}
				case 404:
					error = new NotFoundError('API endpoint');
					errorMessage = 'API endpoint not found';
					break;
				case 422:
					error = new ValidationError(serverErrorMessage || 'Invalid request data');
					errorMessage = serverErrorMessage || 'Invalid request data';
					break;
				case 500:
					// For script execution errors, include more details in the error message
					let detailedMessage = serverErrorMessage || 'Server error';
					if (serverErrorBody?.data) {
						const errorData = serverErrorBody.data;
						// If there are error lines, include the last one in the message
						if (errorData.error_lines && Array.isArray(errorData.error_lines) && errorData.error_lines.length > 0) {
							const lastError = errorData.error_lines[errorData.error_lines.length - 1];
							if (lastError && typeof lastError === 'string' && lastError.trim()) {
								detailedMessage = lastError.length > 200 ? lastError.substring(0, 200) + '...' : lastError;
							}
						}
						// Include return code if available
						if (errorData.return_code !== undefined && errorData.return_code !== null && errorData.return_code !== 0) {
							detailedMessage = `Script execution failed (return code: ${errorData.return_code}): ${detailedMessage}`;
						}
					}
					error = new AppError(detailedMessage, 500);
					errorMessage = detailedMessage;
					break;
				case 502:
				case 503:
				case 504:
					errorMessage = serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`;
					error = new AppError(errorMessage, response.status);
					break;
				default:
					error = new AppError(serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`, response.status);
					errorMessage = serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`;
			}
			
			// Pass both the request body and the server's error response body
			return handleError(error, 'global.ts', { 
				url: fullUrl, 
				status: response.status, 
				body: body_json, // Request body
				errorResponse: serverErrorBody // Server's error response (includes data with error_lines, output_lines, etc.)
			});
		}
	} catch (error) {
		if ((error as Error).name === 'AbortError') {
			return { success: false, message: 'Request cancelled', status: 0, type: 'AbortError' };
		}
		
		const networkError = new NetworkError(`Network error: ${(error as Error).message}`);
		return handleError(networkError, 'global.ts', { url, body: body_json, signal: !!signal });
	}
}

/**
 * Helper function to get event times - always uses POST to avoid URL length limitations
 * @param params - Parameters for the event times request
 * @param signal - Optional AbortSignal for request cancellation
 */
export async function getEventTimes(
	params: {
		class_name: string;
		project_id: number;
		dataset_id?: number;
		source_id?: number;
		date?: string;
		event_list: number[] | string; // Can be array or JSON string
		timezone?: string;
	},
	signal?: AbortSignal
): Promise<ApiResponse> {
	const { apiEndpoints } = await import('../config/env.js');

	// API requires either dataset_id (valid id) OR (source_id and date). Don't send invalid requests.
	const hasDatasetId = params.dataset_id != null && params.dataset_id > 0;
	const hasSourceAndDate = params.source_id != null && params.source_id > 0 && !!params.date;
	if (!hasDatasetId && !hasSourceAndDate) {
		throw new Error('getEventTimes: Either dataset_id > 0 OR (source_id and date) must be provided');
	}
	
	// Convert event_list to JSON string if it's an array
	const eventListString = Array.isArray(params.event_list) 
		? JSON.stringify(params.event_list) 
		: params.event_list;
	
	const body = {
		class_name: params.class_name,
		project_id: params.project_id,
		...(hasDatasetId && { dataset_id: params.dataset_id }),
		...(params.source_id !== undefined && { source_id: params.source_id }),
		...(params.date && { date: params.date }),
		event_list: eventListString,
		...(params.timezone && { timezone: params.timezone })
	};
	
	return postData(`${apiEndpoints.app.events}/times`, body, signal);
}

export async function putData(url: string, body_json: any, signal?: AbortSignal): Promise<ApiResponse> {
	try {
		// Import authManager dynamically to avoid circular dependencies
		const { authManager } = await import('./authManager');
		
		// Construct full URL (same logic as getData and postData)
		let fullUrl: string;
		if (url.startsWith('http')) {
			fullUrl = url;
		} else if (url.startsWith('/api')) {
			// URL already starts with /api, use as-is
			fullUrl = url;
		} else {
			// Prepend API_BASE_URL for relative URLs
			fullUrl = config.API_BASE_URL + url;
		}
		
		const response = await authManager.makeAuthenticatedRequest(fullUrl, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body_json),
			signal: signal
		});

		// Let backend sendResponse handle logging with proper source information
		if (response.ok) {
			// Check if response has content before parsing JSON
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('application/json')) {
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, contentType });
			}
			
			// Check if response body is empty
			const text = await response.text();
			if (!text || text.trim() === '') {
				const error = new AppError('Empty response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status });
			}
			
			try {
				const response_json: ApiResponse = JSON.parse(text);
				return response_json;
			} catch (jsonError) {
				const error = new AppError('Invalid JSON response from server', 502);
				return handleError(error, 'global.ts', { url: fullUrl, status: response.status, jsonError: (jsonError as Error).message });
			}
		} else {
			// Try to extract error message and full response body from response
			// Clone the response so we can read it without consuming it
			const responseClone = response.clone();
			let serverErrorMessage: string | null = null;
			let serverErrorBody: any = null;
			try {
				const contentType = response.headers.get('content-type');
				if (contentType && contentType.includes('application/json')) {
					const text = await responseClone.text();
					if (text && text.trim() !== '') {
						const errorResponse = JSON.parse(text);
						// Server returns { success: false, message: "...", data: {...} } format
						// OR FastAPI HTTPException format: { detail: "..." }
						serverErrorMessage = errorResponse.message || errorResponse.error || errorResponse.detail || null;
						
						// For script execution errors, try to extract more detailed error message from data.error_lines
						if (errorResponse.data?.error_lines && Array.isArray(errorResponse.data.error_lines) && errorResponse.data.error_lines.length > 0) {
							const lastError = errorResponse.data.error_lines[errorResponse.data.error_lines.length - 1];
							if (lastError && typeof lastError === 'string' && lastError.trim()) {
								// Use the last error line as the message if it's more specific than the generic message
								if (!serverErrorMessage || serverErrorMessage === 'Script execution failed' || serverErrorMessage === 'Server error') {
									serverErrorMessage = lastError.length > 300 ? lastError.substring(0, 300) + '...' : lastError;
								}
							}
						}
						
						// Capture the full error response body (includes data with error_lines, output_lines, etc.)
						serverErrorBody = errorResponse;
						
						// Log for debugging. Use summary only — never full errorResponse (avoids huge DB payloads).
						const errorSummaryPut = buildErrorResponseLogSummary(errorResponse, response.status, serverErrorMessage, fullUrl, 'PUT');
						const summaryStrPut = JSON.stringify(errorSummaryPut);
						logError('[global.ts] Server error response captured:', summaryStrPut.length > ERROR_LOG_CONTEXT_MAX_LENGTH ? summaryStrPut.substring(0, ERROR_LOG_CONTEXT_MAX_LENGTH) + '...' : summaryStrPut);
					} else {
						logError('[global.ts] Server returned empty error response body');
					}
				} else {
					const plainBody = (await responseClone.text()).trim();
					const { logMessage, userMessage } = summarizeNonJsonErrorResponse(
						response.status,
						contentType,
						plainBody
					);
					serverErrorMessage = userMessage;
					logError(logMessage);
				}
			} catch (parseError) {
				// If we can't parse the error, log it for debugging
				logError('[global.ts] Error parsing server error response:', parseError);
			}

			const streamSoftFailPut = await tryStreamSoftFailResponse(
				fullUrl,
				response,
				'putData',
				serverErrorMessage
			);
			if (streamSoftFailPut) {
				return streamSoftFailPut;
			}
			
			// Handle specific HTTP errors consistently
			let error: AppError;
			switch (response.status) {
				case 401: {
					try {
						const { authManager } = await import('./authManager');
						const authResult = await handleUnauthorizedWithResilientRefresh({
							fullUrl,
							retryRequest: () =>
								authManager.makeAuthenticatedRequest(fullUrl, {
									method: 'PUT',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify(body_json),
									signal,
								}),
						});

						if (authResult.recovered && authResult.response) {
							const response_json: ApiResponse = await authResult.response.json();
							return response_json;
						}

						if (authResult.abandonSession) {
							await abandonUserSession(fullUrl);
						}
					} catch (redirectErr) {
						logError('Failed to redirect to login:', redirectErr);
					}

					error = new AuthError('Authentication required');
					break;
				}
				case 404:
					error = new NotFoundError('API endpoint');
					break;
				case 422:
					error = new ValidationError('Invalid request data');
					break;
				case 500:
					error = new AppError('Server error', 500);
					break;
				case 502:
				case 503:
				case 504: {
					const msg = serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`;
					error = new AppError(msg, response.status);
					break;
				}
				default:
					error = new AppError(serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`, response.status);
			}
			
			// Pass both the request body and the server's error response body
			return handleError(error, 'global.ts', { 
				url: fullUrl, 
				status: response.status, 
				body: body_json, // Request body
				errorResponse: serverErrorBody // Server's error response (includes data with error_lines, output_lines, etc.)
			});
		}
	} catch (error) {
		if ((error as Error).name === 'AbortError') {
			return { success: false, message: 'Request cancelled', status: 0, type: 'AbortError' };
		}
		
		const networkError = new NetworkError(`Network error: ${(error as Error).message}`);
		return handleError(networkError, 'global.ts', { url, body: body_json, signal: !!signal });
	}
}

/**
 * Media Container Scaling Utility
 * 
 * Sets up dynamic scaling for media-container elements that accounts for:
 * - Browser zoom (via visualViewport API)
 * - Sidebar width (275px expanded, 64px collapsed)
 * - Header height (60px)
 * - Both width and height scaling
 * 
 * @param options Configuration options
 * @param options.baseWidth Base width for scaling calculation (default: 1620)
 * @param options.baseHeight Base height for scaling calculation (default: 1020 - approx 1080p minus header)
 * @param options.headerHeight Header height in pixels (default: 60)
 * @param options.sidebarExpandedWidth Sidebar width when expanded (default: 275)
 * @param options.sidebarCollapsedWidth Sidebar width when collapsed (default: 64)
 * @param options.scaleToWidth If true, scale based on width only (ignores height constraint). If false, uses minimum of width/height (default: false)
 * @param options.logPrefix Optional prefix for debug logs
 * @param options.onScale Optional callback function called when scale updates
 * @returns Cleanup function to remove event listeners and observers
 */

/** Custom event name for split panel resize (divider drag). Dispatch from Dashboard so scaling updates. */
export const SPLIT_PANEL_RESIZE_EVENT = 'split-panel-resize';

/** Call when the split view divider is dragged so media-container scaling can update. */
export function notifySplitPanelResize(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent(SPLIT_PANEL_RESIZE_EVENT));
    });
  });
}

export function setupMediaContainerScaling(options: {
  baseWidth?: number;
  baseHeight?: number;
  headerHeight?: number;
  sidebarExpandedWidth?: number;
  sidebarCollapsedWidth?: number;
  scaleToWidth?: boolean;
  logPrefix?: string;
  onScale?: (scaleFactor: number, containerWidth: number, containerHeight: number) => void;
  /** When set, used instead of document.getElementById('media-container') (e.g. multiple panels). */
  getMediaContainer?: () => HTMLElement | null;
  /**
   * Headerless ManeuverWindow popup: use #main-content client box (not window.innerHeight) and do not
   * apply maneuvers-page vertical reserves meant for the main dashboard (control bar + table strip).
   */
  soloManeuverWindow?: boolean;
  /**
   * Added to maneuvers-page layout height when `.maneuver-window-media-container` (e.g. TIME SERIES
   * in solo window) so charts aren't short by a phantom toolbar band.
   */
  maneuverWindowExtraLayoutHeightPx?: number;
  /**
   * Solo ManeuverWindow SCATTER: match full Maneuvers report — page scroll on #main-content,
   * not inner scroll on #maneuver-plots (see docs/frontend/maneuver-window-scaling.md).
   */
  maneuverWindowReportScroll?: boolean;
} = {}): () => void {
  const {
    baseWidth = 1620,
    baseHeight = 1020,
    headerHeight = 60,
    sidebarExpandedWidth = 275,
    sidebarCollapsedWidth = 64,
    scaleToWidth = false,
    logPrefix = 'MediaContainer',
    onScale,
    getMediaContainer,
    soloManeuverWindow = false,
    maneuverWindowExtraLayoutHeightPx = 0,
    maneuverWindowReportScroll = false
  } = options;

  let updateScaleRafId: number | null = null;
  let lastAppliedLayoutSignature = '';
  let lastLegendHeightForScaling = -1;
  let dimensionCheckInterval: ReturnType<typeof setInterval> | null = null;
  let perfPollingStopTimeout: ReturnType<typeof setTimeout> | null = null;
  let reportContentObserver: MutationObserver | null = null;
  const LAYOUT_EPSILON_PX = 4;

  const isPerformanceScalingContainer = (el: HTMLElement): boolean =>
    el.classList.contains('performance-page') ||
    el.classList.contains('fleet-performance-page') ||
    el.classList.contains('performance-history-report');

  /**
   * Solo ManeuverWindow: the shared color legend row above the view.
   *
   * Was `#scatter-area .legend` / `#timeseries-area .legend`, which never matched anything — every
   * caller below is gated on the container carrying `maneuver-window-media-container`, and
   * ManeuverWindow puts that class only on its TABLE container, which holds neither area. The old
   * 56px fallback was therefore always what got reserved.
   */
  const getManeuverWindowLegendElement = (container: HTMLElement): HTMLElement | null => {
    const el = container.querySelector('#maneuver-color-legend-row');
    return el instanceof HTMLElement ? el : null;
  };

  /** 0, not a guess: no legend mounted means no space to reserve for one. */
  const getManeuverWindowLegendHeight = (container: HTMLElement): number => {
    const legendEl = getManeuverWindowLegendElement(container);
    return legendEl ? Math.ceil(legendEl.getBoundingClientRect().height) : 0;
  };

  /**
   * Per docs/frontend/performance-page-scroll-fix.md and maneuver-window-scaling.md:
   * layoutHeight = desiredVisibleHeight / scaleFactor on inner scroll hosts.
   */
  const applyManeuverWindowScrollViewport = (
    container: HTMLElement,
    scale: number,
    availHeight: number,
    extraLayoutPaddingPx: number
  ): void => {
    if (!container.classList.contains('maneuver-window-media-container')) return;

    const legendHeight = getManeuverWindowLegendHeight(container);
    const scrollPadding = 20 + extraLayoutPaddingPx;
    const desiredVisibleHeight = Math.max(200, availHeight - legendHeight - scrollPadding);
    const safeScale = Math.max(scale, 0.05);
    const layoutHeight = Math.round(desiredVisibleHeight / safeScale);

    const applyToScrollHost = (el: HTMLElement | null): void => {
      if (!el) return;
      el.style.setProperty('height', `${layoutHeight}px`, 'important');
      el.style.setProperty('max-height', `${layoutHeight}px`, 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.setProperty('overflow-y', 'auto', 'important');
      el.style.setProperty('overflow-x', 'hidden', 'important');
    };

    const clearScrollHost = (el: HTMLElement | null): void => {
      if (!el) return;
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('min-height');
      el.style.removeProperty('overflow-y');
      el.style.removeProperty('overflow-x');
    };

    // SCATTER in report-scroll mode: page scroll on #main-content (same as Maneuvers report).
    clearScrollHost(
      container.querySelector('#scatter-area #maneuver-plots') as HTMLElement | null
    );
    const groupedScatterRoot = container.querySelector(
      '#scatter-area .maneuver-scatter-grouped-root'
    );
    if (groupedScatterRoot?.lastElementChild instanceof HTMLElement) {
      clearScrollHost(groupedScatterRoot.lastElementChild);
    }

    container.querySelectorAll('#timeseries-area .maneuver-ts-plots-root').forEach((node) => {
      applyToScrollHost(node as HTMLElement);
    });
  };

  const clearManeuverWindowReportScrollHosts = (container: HTMLElement): void => {
    const plots = container.querySelector('#scatter-area #maneuver-plots') as HTMLElement | null;
    if (plots) {
      plots.style.removeProperty('height');
      plots.style.removeProperty('max-height');
      plots.style.removeProperty('min-height');
      plots.style.removeProperty('overflow-y');
      plots.style.removeProperty('overflow-x');
    }
    container.querySelectorAll('#timeseries-area .maneuver-ts-plots-root').forEach((node) => {
      const el = node as HTMLElement;
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('min-height');
      el.style.removeProperty('overflow-y');
      el.style.removeProperty('overflow-x');
    });
  };

  const applyManeuverWindowMediaContainerLayout = (
    container: HTMLElement,
    layoutWidth: number,
    layoutHeight: number
  ): void => {
    container.style.setProperty('width', `${layoutWidth}px`, 'important');
    container.style.setProperty('min-width', `${layoutWidth}px`, 'important');
    container.style.setProperty('height', `${layoutHeight}px`, 'important');
    container.style.setProperty('min-height', `${layoutHeight}px`, 'important');
  };

  const scheduleUpdateScale = () => {
    if (updateScaleRafId != null) return;
    updateScaleRafId = requestAnimationFrame(() => {
      updateScaleRafId = null;
      updateScale();
    });
  };

  /**
   * Report pages render their whole `#media-container > .container` subtree (legend + scroll host)
   * only after their data has loaded, which is usually after every scale pass scheduled at mount has
   * already run — and they remount it whenever they rebuild charts (axis/plot-type change). Watch the
   * media container's direct children so we re-scale each time that subtree appears. childList only,
   * no subtree: chart rendering churn lives deeper in the tree and never reaches this callback.
   */
  const watchReportContentMount = (container: HTMLElement): void => {
    if (reportContentObserver || typeof MutationObserver === 'undefined') return;
    reportContentObserver = new MutationObserver(() => scheduleUpdateScale());
    reportContentObserver.observe(container, { childList: true });
  };

  const updateScale = () => {
    const mediaContainer = getMediaContainer?.() ?? document.getElementById('media-container');
    if (!mediaContainer) return;
    
    const parentContainer = mediaContainer.parentElement;
    if (!parentContainer) return;

    const isManeuverWindowMedia = mediaContainer.classList.contains('maneuver-window-media-container');
    
    // Check if we're inside a split-panel
    const splitPanel = mediaContainer.closest('.split-panel');
    const isInSplitView = !!splitPanel;
    
    let availableWidth: number;
    let availableHeight: number;
    
    if (isInSplitView && splitPanel) {
      // In split view: use the actual panel dimensions (panel may not be laid out yet on first run)
      availableWidth = splitPanel.clientWidth;
      availableHeight = splitPanel.clientHeight;
      if (availableWidth < 100 || availableHeight < 100) {
        const rect = splitPanel.getBoundingClientRect();
        if (rect.width >= 100 && rect.height >= 100) {
          availableWidth = Math.round(rect.width);
          availableHeight = Math.round(rect.height);
        } else {
          const splitContent = splitPanel.parentElement?.closest('.split-view-content');
          if (splitContent && splitContent.clientWidth >= 100 && splitContent.clientHeight >= 100) {
            availableWidth = splitContent.clientWidth;
            availableHeight = splitContent.clientHeight;
          }
        }
      }
      if (availableWidth < 100 || availableHeight < 100) {
        warn(`${logPrefix} - Split panel dimensions not ready (${availableWidth}x${availableHeight}px), will retry`);
        return;
      }
    } else {
      // Normal view: use main-content dimensions directly (it already accounts for sidebar)
      const mainContent = document.getElementById('main-content') || parentContainer;
      const windowRoot = document.getElementById('window');

      // ManeuverWindow #media-container: use #window (or visualViewport when narrow) — not #main-content,
      // which scaling-page caps at 100vh-60px and under-fills the popup.
      if (isManeuverWindowMedia && windowRoot && windowRoot.clientHeight >= 100) {
        const narrow = window.innerWidth <= 1000;
        const vv = window.visualViewport;
        if (narrow && vv && (vv.height || 0) >= 100) {
          availableWidth = Math.max(100, Math.round(vv.width || windowRoot.clientWidth));
          availableHeight = Math.max(100, Math.round(vv.height) - headerHeight);
        } else {
          availableWidth = Math.max(100, windowRoot.clientWidth);
          availableHeight = Math.max(100, windowRoot.clientHeight - headerHeight);
        }
      } else if (soloManeuverWindow && !maneuverWindowReportScroll && mainContent.clientHeight > 0 && mainContent.clientWidth > 0) {
        availableWidth = mainContent.clientWidth;
        availableHeight = Math.max(100, mainContent.clientHeight - headerHeight);
      } else {
        // Get viewport height for height calculation
        const isMobile = window.innerWidth <= 1000;
        let viewportHeight: number;

        if (window.visualViewport && isMobile) {
          // On mobile, prefer visualViewport for accurate visible dimensions
          viewportHeight = window.visualViewport.height || window.innerHeight || (mainContent.clientHeight > 0 ? mainContent.clientHeight : 1280);
        } else {
          // On desktop or when visualViewport unavailable, use window dimensions
          viewportHeight = window.innerHeight || (mainContent.clientHeight > 0 ? mainContent.clientHeight : 1280);
        }

        // Use main-content's clientWidth (excludes padding/borders, gives inner width)
        // This is more accurate than getBoundingClientRect which includes padding/borders
        if (mainContent.clientWidth > 0) {
          availableWidth = mainContent.clientWidth;
        } else {
          // Fallback: use getBoundingClientRect if clientWidth not available
          const mainContentRect = mainContent.getBoundingClientRect();
          if (mainContentRect.width > 0) {
            availableWidth = mainContentRect.width;
          } else {
            // Final fallback: use parent container width
            const parentRect = parentContainer.getBoundingClientRect();
            availableWidth = parentRect.width > 0 ? parentRect.width : Math.max(100, window.innerWidth - 275);
          }
        }

        // Subtract header height from available height
        availableHeight = Math.max(100, viewportHeight - headerHeight);
      }
    }

    // Maneuvers page: reserve minimal height so table + Clear Formatting isn't cut off in big table mode
    if (
      mediaContainer.classList.contains('maneuvers-page') &&
      !soloManeuverWindow &&
      !isManeuverWindowMedia
    ) {
      availableHeight = Math.max(100, availableHeight - 60);
    }

    // Race Summary and Prestart (Start Summary): same full-width/full-height treatment (use full available space)
    const isRaceSummaryPage = mediaContainer.classList.contains('race-summary-page');
    const isPrestartPage = mediaContainer.classList.contains('prestart-page');
    const isCheatSheetPage = mediaContainer.classList.contains('cheat-sheet-page');
    /**
     * Datasets: one screen tall, with the table scrolling inside it. Was the class-less
     * fallback container below, whose layout height is the visible height *before* transform:
     * scale() — so the page painted short while `.datasets-page` (a hard 1280px min-height) hung
     * out the bottom and #main-content scrolled the whole thing.
     */
    const isDatasetsPage = mediaContainer.classList.contains('datasets-page-container');
    // Cheat Sheet page: reserve bottom padding (32px) so content isn't cut off
    if (isCheatSheetPage) {
      availableHeight = Math.max(100, availableHeight - 32);
    }

    // Layout base width: at or above 1620px *available* main-area width, grow the layout with the panel;
    // when the main area is narrower than 1620 (e.g. 1620px window minus sidebar), keep a 1620px layout
    // and scale down with transform — same as sub-1620 windows. A former maneuvers-only branch used
    // availableWidth whenever window.innerWidth >= 1620, which forced scale 1 at ~1345px and looked squashed.
    const isManeuversPage = mediaContainer.classList.contains('maneuvers-page');
    const effectiveBaseWidth =
      availableWidth >= baseWidth ? availableWidth : baseWidth;
    document.documentElement.style.setProperty('--layout-base-width', `${effectiveBaseWidth}px`);

    const isMobile = window.innerWidth <= 1000;

    // Always set container to fill available space
    // Performance / scatter / targets pages are fitted with transform: scale(), so their layout height
    // is visibleHeight / scaleFactor (scaledPageLayoutHeightPx below)
    // For timeseries pages, use viewport height directly (content will scroll inside)
    const isFleetPerformancePage = mediaContainer.classList.contains('fleet-performance-page');
    const isTargetsPage = mediaContainer.classList.contains('targets-page');
    const isPerformancePage = mediaContainer.classList.contains('performance-page') ||
                              isFleetPerformancePage ||
                              mediaContainer.classList.contains('scatter-page') ||
                              isTargetsPage;
    const isTimeseriesPage = mediaContainer.classList.contains('timeseries-page');
    const isCalibrationPage = mediaContainer.classList.contains('calibration-page');
    const isFleetCalibrationPage = mediaContainer.classList.contains('fleet-calibration-page');
    /** Long scrollable dataset reports that use the same layout height / scale math as Explore TimeSeries */
    const isTimeseriesLikePage = isTimeseriesPage || isCalibrationPage || isFleetCalibrationPage;
    /**
     * The real visible box: the split panel, or the viewport minus the header. Pages that fit
     * themselves with `transform: scale()` divide this by the scale factor to get a layout height
     * whose *painted* height fills exactly the visible box — see
     * docs/frontend/page-scaling-strategy.md ("layoutHeight = desiredVisibleHeight / scaleFactor").
     */
    const visibleFillHeight = Math.max(200, availableHeight);
    /**
     * Layout height for a `transform: scale()`-fitted page: painted height = layoutHeight * scale,
     * so leaving such a container on CSS `height: 100%` paints only `scale` of the viewport (blank
     * band below) while its taller inner scroll host is clipped by the container's overflow: hidden.
     * Mirrors the scaleFactor computed further down (these pages all scale to width, capped at 1.5).
     */
    const scaledPageLayoutHeightPx = (): number => {
      const layoutScale = Math.max(Math.min(availableWidth / effectiveBaseWidth, 1.5), 0.1);
      // floor, never round: layoutHeight * scale must not EXCEED the visible height, or the scaled box
      // overflows #main-content (overflow-y: auto) by a fraction of a pixel. That can flip on an outer
      // scrollbar, which drops #main-content.clientWidth by 8px, which changes the scale and the layout
      // signature, which the #main-content ResizeObserver then re-runs — an oscillation.
      return Math.max(200, Math.floor(visibleFillHeight / layoutScale));
    };
    
    if (isTimeseriesLikePage) {
		// Timeseries / Calibration: use viewport height directly
		// Content will scale to fit width, and overflow will scroll vertically inside the scroll container
		// On mobile, use visualViewport height for accurate calculation
		const viewportHeightForCalc = (window.visualViewport && isMobile) 
			? (window.visualViewport.height || window.innerHeight)
			: window.innerHeight;
		availableHeight = viewportHeightForCalc - headerHeight;
    }

	// Use effectiveBaseWidth so when viewport >= 1620 content fills screen; when narrow CSS var is 1620
	if (isTimeseriesLikePage) {
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		// Layout height must compensate for transform scale so visible height = availableHeight (layoutHeight * scaleFactor = availableHeight)
		const timeseriesScaleFactor = Math.min(availableWidth / effectiveBaseWidth, 1.5);
		const safeScale = Math.max(timeseriesScaleFactor, 0.1);
		const layoutHeight = Math.max(200, Math.round(availableHeight / safeScale));
		mediaContainer.style.setProperty('height', `${layoutHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${layoutHeight}px`, 'important');
		mediaContainer.style.setProperty('max-height', `${layoutHeight}px`, 'important');
		mediaContainer.style.setProperty('overflow-y', 'auto', 'important');
	} else if (isFleetPerformancePage) {
		// Same width/min-width as performance-page: fixed layout width = effectiveBaseWidth; transform:scale()
		// fits it to main-content. Removing inline width relied on #main-content>#media-container (often false
		// when wrapped in .playback-view-wrapper), so width could fail to track --layout-base-width on resize.
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		// Explicit layout height that compensates for transform: scale(), mobile and desktop alike.
		// Desktop used to drop the inline height and fall back to CSS `height: 100%`, which is one
		// viewport of *layout* — so below 1620px only scale*(100vh-60) was painted (blank band) and
		// the taller .performance-charts-scroll-container below was clipped by overflow: hidden.
		const fleetLayoutHeight = scaledPageLayoutHeightPx();
		mediaContainer.style.setProperty('height', `${fleetLayoutHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${fleetLayoutHeight}px`, 'important');
	} else if (mediaContainer.classList.contains('performance-page') || mediaContainer.classList.contains('scatter-page')) {
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		// Same inverse-scale layout height as fleet-performance: painted height == the visible box. This
		// used to be the heightMultiplier ladder (1.8-10x viewport), which painted ~1.9 viewports inside a
		// one-viewport #main-content: an outer scrollbar over the dead space below the (correctly sized,
		// max-height-capped) scroll host.
		const perfLayoutHeight = scaledPageLayoutHeightPx();
		mediaContainer.style.setProperty('height', `${perfLayoutHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${perfLayoutHeight}px`, 'important');
    } else if (mediaContainer.classList.contains('maneuvers-page')) {
		const maneuversLayoutScale = Math.min(availableWidth / effectiveBaseWidth, 1.5);
		const safeManeuversScale = Math.max(maneuversLayoutScale, 0.1);
		const maneuverWindowLayoutBoost =
			isManeuverWindowMedia && maneuverWindowExtraLayoutHeightPx > 0
				? maneuverWindowExtraLayoutHeightPx
				: 0;
		const maneuversLayoutHeight = Math.max(
			200,
			Math.round((availableHeight + maneuverWindowLayoutBoost) / safeManeuversScale)
		);
		applyManeuverWindowMediaContainerLayout(
			mediaContainer,
			effectiveBaseWidth,
			maneuversLayoutHeight
		);
		// Solo ManeuverWindow SCATTER (no .maneuver-window-media-container): page scroll like full report
		if (!mediaContainer.classList.contains('maneuver-window-media-container')) {
			clearManeuverWindowReportScrollHosts(mediaContainer);
		}
	} else if (isRaceSummaryPage || isPrestartPage) {
		// Same as fleet-performance-page: fixed layout width; transform: scale() fits to main-content,
		// and the same scale-compensated layout height so a narrow window is filled, not clipped.
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		const summaryLayoutHeight = scaledPageLayoutHeightPx();
		mediaContainer.style.setProperty('height', `${summaryLayoutHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${summaryLayoutHeight}px`, 'important');
	} else if (isCheatSheetPage) {
		// Cheat Sheet: set dimensions via JS; add header height so container fills (was short by header amount)
		const cheatSheetHeight = availableHeight + headerHeight;
		mediaContainer.style.removeProperty('width');
		mediaContainer.style.removeProperty('min-width');
		mediaContainer.style.setProperty('height', `${cheatSheetHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${cheatSheetHeight}px`, 'important');
		mediaContainer.style.setProperty('max-height', `${cheatSheetHeight}px`, 'important');
	} else if (isTargetsPage) {
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		// Targets page-scrolls: its #media-container is overflow: visible with no inner scroll host, so
		// the #main-content scrollbar tracks the children overflowing it. Give it a scale-compensated
		// FLOOR so a short page still fills the window, and let taller content grow the box — height:auto
		// explicitly, because the shared rule (Styles.css `#main-content>#media-container.targets-page`)
		// would otherwise pin it to one viewport of layout, i.e. `scale` of a viewport painted.
		mediaContainer.style.setProperty('height', 'auto', 'important');
		mediaContainer.style.setProperty('min-height', `${scaledPageLayoutHeightPx()}px`, 'important');
	} else if (isDatasetsPage) {
		// Bounded to exactly one visible screen, top and bottom: the table wrapper is the only
		// scroll host, and it can only claim the leftover height if the page it sits in has a
		// height of its own. Scale-compensated for the same reason as every page above —
		// painted height = layoutHeight * scaleFactor (page-scaling-strategy.md).
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		const datasetsLayoutHeight = scaledPageLayoutHeightPx();
		mediaContainer.style.setProperty('height', `${datasetsLayoutHeight}px`, 'important');
		mediaContainer.style.setProperty('min-height', `${datasetsLayoutHeight}px`, 'important');
		mediaContainer.style.setProperty('max-height', `${datasetsLayoutHeight}px`, 'important');
	} else {
		// Other pages (e.g. the class-less Parallel media container)
		mediaContainer.style.setProperty('width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('min-width', `${effectiveBaseWidth}px`, 'important');
		mediaContainer.style.setProperty('height', `${availableHeight}px`, 'important');
	}
    
    // Scale factor: use effectiveBaseWidth so when viewport >= 1620 scale is 1
    const widthScaleFactor = availableWidth / effectiveBaseWidth;
    const heightScaleFactor = availableHeight / baseHeight;
    
    // Use width-based scaling if requested, or for maneuvers page so scale grows continuously with width (no plateau 1370–1620)
    // Width-based scaling fills the width completely (may require vertical scrolling)
    // Minimum scaling ensures content fits in both dimensions (maintains aspect ratio)
    // fleet-performance is listed explicitly (not just via its scaleToWidth callers) so the
    // scale stays provably equal to the availableWidth/effectiveBaseWidth ratio that
    // scaledPageLayoutHeightPx above divides by — a future caller that forgets scaleToWidth
    // would otherwise get a layout height computed from a scale it isn't rendered at.
    // isDatasetsPage: scaledPageLayoutHeightPx above divides by the width ratio, so the page must
    // actually be rendered at it — the min(width, height) default would paint short of the box.
    const useWidthBasedScale =
      scaleToWidth || isManeuversPage || isRaceSummaryPage || isPrestartPage || isFleetPerformancePage ||
      isDatasetsPage;
    let scaleFactor = useWidthBasedScale ? widthScaleFactor : Math.min(widthScaleFactor, heightScaleFactor);
    
    // IMPORTANT: Cap scale factor appropriately based on page type
    // Scatter/probability pages: Never scale up beyond 100% (keep charts at base size)
    // Performance pages: Allow scaling up to fill larger monitors, but cap at reasonable maximum (1.5x)
    // Timeseries pages: Allow scaling up to fill larger monitors, but cap at reasonable maximum (1.5x)
    // Other pages: Cap at 1.0
    const isScatterOrProbabilityPage = mediaContainer.classList.contains('scatter-page');
    if (isScatterOrProbabilityPage) {
      scaleFactor = Math.min(scaleFactor, 1.0); // Never scale up beyond 100% for scatter/probability
    }
    // Performance pages and timeseries pages: allow scaling up to fill larger monitors, but cap at reasonable maximum
    else if (isPerformancePage || isTimeseriesLikePage) {
      scaleFactor = Math.min(scaleFactor, 1.5); // Cap at 150% for very large monitors (prevents excessive scaling)
    }
    // Maneuvers: allow scale up to fill width on large monitors (same as performance)
    else if (mediaContainer.classList.contains('maneuvers-page')) {
      scaleFactor = Math.min(scaleFactor, 1.5);
    }
    // Race Summary / Prestart / Training Summary: width-based scale like fleet-performance and maneuvers (cap zoom-up)
    else if (isRaceSummaryPage || isPrestartPage) {
      scaleFactor = Math.min(scaleFactor, 1.5);
    }
    // Cheat Sheet: no transform scaling - height set via JS above
    else if (isCheatSheetPage) {
      scaleFactor = 1;
    }
    // Other pages: cap at 1.0
    else {
      scaleFactor = Math.min(scaleFactor, 1.0);
    }

    const legendElForSig = mediaContainer.querySelector('.performance-legend-section') as HTMLElement | null;
    const legendHeightForSig = legendElForSig
      ? Math.round(legendElForSig.getBoundingClientRect().height)
      : 0;
    const maneuverLegendHeightForSig = isManeuverWindowMedia
      ? getManeuverWindowLegendHeight(mediaContainer)
      : 0;
    const mainContentElForSig = document.getElementById('main-content');
    const layoutSignature = [
      Math.round(effectiveBaseWidth),
      Math.round(availableWidth),
      Math.round(availableHeight),
      scaleFactor.toFixed(5),
      legendHeightForSig,
      maneuverLegendHeightForSig,
      isMobile ? 1 : 0,
      mediaContainer.className,
      mainContentElForSig?.clientWidth ?? 0,
    ].join('|');

    // Scroll host (`.performance-charts-scroll-container`) exists only once the report has rendered
    // its charts, so the mount-time passes (rAF / 50 / 200 / 400 / 1000ms) can all run while the
    // page still shows <Loading/>. Without this the viewport height below is never applied and the
    // host falls back to CSS `height: 100%` of the deliberately over-tall media container, so the
    // scrollbar only spans the first screen of charts. Never let the signature guard swallow the
    // first pass that can actually size it.
    const usesJsScrollHostHeight =
      mediaContainer.classList.contains('performance-page') ||
      mediaContainer.classList.contains('fleet-performance-page') ||
      mediaContainer.classList.contains('scatter-page') ||
      isTimeseriesLikePage ||
      isRaceSummaryPage ||
      isPrestartPage ||
      isCheatSheetPage;
    const scrollHostEl = usesJsScrollHostHeight
      ? (mediaContainer.querySelector('.performance-charts-scroll-container') as HTMLElement | null)
      : null;
    const scrollHostNeedsHeight = usesJsScrollHostHeight && !scrollHostEl?.style.height;
    if (usesJsScrollHostHeight) {
      watchReportContentMount(mediaContainer);
    }
    // Legend polling in setupLegendResizeObserver gives up after ~2s; attach here when a slow data
    // load renders the legend later, so legend-height changes still resize the scroll viewport.
    if (!legendResizeObserver && mediaContainer.querySelector('.performance-legend-section')) {
      setupLegendResizeObserver(mediaContainer);
    }

    if (layoutSignature === lastAppliedLayoutSignature && !scrollHostNeedsHeight) {
      return;
    }
    lastAppliedLayoutSignature = layoutSignature;

    // Fleet performance + race summary / prestart layout height is set with the container width
    // above (scaledPageLayoutHeightPx), i.e. before the layoutSignature early-return — an
    // assignment placed here would be skipped on any pass the guard short-circuits.

    // Per page-scaling-strategy.md: main-content scrolls (body.scaling-page gives overflow-y: auto)
    // For performance pages, do not set main-content height - CSS scaling-page rule applies
    if (isPerformancePage) {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.style.removeProperty('height');
        }
        // Set scroll container height so the scroll viewport is bounded (prevents scrollbar overflow)
        // Same formula as timeseries: layoutHeight = desiredVisibleHeight / scaleFactor (per maneuver-timeseries-scroll-fix.md)
        // Apply to performance-page, fleet-performance-page, and scatter-page (explore Probability and Scatter; per performance-page-scroll-fix.md)
        const isPerfOrFleetPerf = mediaContainer.classList.contains('performance-page') || mediaContainer.classList.contains('fleet-performance-page');
        const isScatterOrProbability = mediaContainer.classList.contains('scatter-page');
        if (isPerfOrFleetPerf || isScatterOrProbability) {
            const legendEl = mediaContainer.querySelector('.performance-legend-section');
            const legendHeight = legendEl ? (legendEl as HTMLElement).getBoundingClientRect().height : (isScatterOrProbability ? 0 : 50);
            const viewportHeightForScroll = (window.visualViewport && isMobile)
                ? (window.visualViewport.height || window.innerHeight)
                : window.innerHeight;
            const desiredVisibleHeight = Math.max(200, viewportHeightForScroll - headerHeight - legendHeight - 20);
            const layoutHeight = desiredVisibleHeight / scaleFactor;
            const scrollContainer = mediaContainer.querySelector('.performance-charts-scroll-container');
            if (scrollContainer) {
                (scrollContainer as HTMLElement).style.setProperty('height', `${layoutHeight}px`, 'important');
                (scrollContainer as HTMLElement).style.setProperty('max-height', `${layoutHeight}px`, 'important');
            }
        }
    } else if (isTimeseriesLikePage) {
        // For timeseries / calibration, don't constrain main-content height - let it expand with content
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.style.setProperty('height', 'auto', 'important');
            mainContent.style.setProperty('min-height', `${availableHeight}px`, 'important');
        }
        
        // Set scroll container height to enable scrolling
        // Since the scroll container is inside a scaled parent, we need to account for scale factor
        // Formula: layoutHeight = desiredVisibleHeight / scaleFactor
        const scrollContainer = mediaContainer.querySelector('.performance-charts-scroll-container');
        if (scrollContainer) {
            const desiredVisibleHeight = availableHeight; // Viewport height minus header
            const layoutHeight = desiredVisibleHeight / scaleFactor;
            (scrollContainer as HTMLElement).style.setProperty('height', `${layoutHeight}px`, 'important');
            (scrollContainer as HTMLElement).style.setProperty('max-height', `${layoutHeight}px`, 'important');
        }
    } else if (isRaceSummaryPage || isPrestartPage) {
        // Race Summary / Prestart: same scroll pattern as FleetPerformance (per performance-page-scroll-fix.md)
        // No legend; layoutHeight = desiredVisibleHeight / scaleFactor (transform scale)
        const scrollContainer = mediaContainer.querySelector('.performance-charts-scroll-container');
        if (scrollContainer) {
            const viewportHeightForScroll = (window.visualViewport && isMobile)
                ? (window.visualViewport.height || window.innerHeight)
                : window.innerHeight;
            const desiredVisibleHeight = Math.max(200, viewportHeightForScroll - headerHeight - 20);
            const layoutHeight = desiredVisibleHeight / scaleFactor;
            (scrollContainer as HTMLElement).style.setProperty('height', `${layoutHeight}px`, 'important');
            (scrollContainer as HTMLElement).style.setProperty('max-height', `${layoutHeight}px`, 'important');
        }
    } else if (isCheatSheetPage) {
        // Cheat Sheet: set scroll container height via JS; use full viewport minus padding only (main-content is already below header)
        const scrollContainer = mediaContainer.querySelector('.performance-charts-scroll-container');
        if (scrollContainer) {
            const viewportHeightForScroll = (window.visualViewport && isMobile)
                ? (window.visualViewport.height || window.innerHeight)
                : window.innerHeight;
            const desiredVisibleHeight = Math.max(200, viewportHeightForScroll - 32 - 20);
            const layoutHeight = desiredVisibleHeight / scaleFactor;
            (scrollContainer as HTMLElement).style.setProperty('height', `${layoutHeight}px`, 'important');
            (scrollContainer as HTMLElement).style.setProperty('max-height', `${layoutHeight}px`, 'important');
        }
    } else if (isManeuverWindowMedia && mediaContainer.classList.contains('maneuvers-page')) {
        if (maneuverWindowReportScroll) {
          clearManeuverWindowReportScrollHosts(mediaContainer);
        } else {
          applyManeuverWindowScrollViewport(
            mediaContainer,
            scaleFactor,
            availableHeight,
            maneuverWindowExtraLayoutHeightPx
          );
        }
    }
    
    /* Apply the scale factor (will scale both width and height proportionally via transform).
     *
     * removeProperty FIRST. `--scale-factor` is a single global on documentElement that every
     * scaled page shares, and this is the only writer that computes it — but a plain
     * `setProperty(name, value)` is specified to overwrite an existing `important` priority, and
     * engines have not always agreed on that for *custom* properties. The Datasets page used to
     * pin `--scale-factor: 1 !important` here on narrow viewports and never remove it, so on an
     * engine that kept the priority, every report page visited afterwards rendered its 1620px
     * canvas at scale 1 — full size, top-left corner only. That reproduced on a real iPhone and
     * not in Chrome's device emulation, because emulation is still Blink.
     *
     * The write on the Datasets side is gone, but removing the property first makes the value
     * here independent of how any engine resolves priority, and of anything that writes this
     * variable in future. removeProperty clears value and priority together, everywhere. */
    document.documentElement.style.removeProperty('--scale-factor');
    document.documentElement.style.setProperty('--scale-factor', scaleFactor.toString());
    
    // Call optional callback
    if (onScale) {
      onScale(scaleFactor, availableWidth, availableHeight);
    }
    
    // Remove opacity class once scaling is fully applied to prevent flash
    mediaContainer.classList.remove('scaling-initializing');
    
    // Debug logging disabled in production to reduce console noise
  };
  
  // Add scaling-page class to body and html to allow overflow for scaled content
  document.body.classList.add('scaling-page');
  document.documentElement.classList.add('scaling-page');
  
  // Hide container initially to prevent flash, then show after scaling is applied
  const mediaContainer = document.getElementById('media-container');
  if (mediaContainer) {
    mediaContainer.classList.add('scaling-initializing');
  }
  
  // Retry logic for mobile: keep trying until media-container is found
  let retryCount = 0;
  const maxRetries = 20; // Try for up to 2 seconds (20 * 100ms)
  
  const tryUpdateScale = () => {
    const container = getMediaContainer?.() ?? document.getElementById('media-container');
    if (container) {
      scheduleUpdateScale();
    } else if (retryCount < maxRetries) {
      retryCount++;
      setTimeout(tryUpdateScale, 100);
    } else {
      warn(`${logPrefix} - media-container not found after ${maxRetries} retries`);
    }
  };
  
  requestAnimationFrame(() => {
    tryUpdateScale();
    setTimeout(() => scheduleUpdateScale(), 50);
    setTimeout(() => scheduleUpdateScale(), 200);
  });
  
  const resolveResizeObserveTarget = (): Element | null => {
    const mediaContainerEl = getMediaContainer?.() ?? document.getElementById('media-container');
    if (!mediaContainerEl) return null;
    if (isPerformanceScalingContainer(mediaContainerEl)) {
      return document.getElementById('main-content') ?? mediaContainerEl.parentElement ?? mediaContainerEl;
    }
    if (mediaContainerEl.classList.contains('maneuver-window-media-container')) {
      return (
        document.getElementById('main-content') ??
        mediaContainerEl.closest('.maneuver-window-fill-root') ??
        mediaContainerEl.parentElement ??
        mediaContainerEl
      );
    }
    return mediaContainerEl;
  };

  const resizeObserver = new ResizeObserver(() => scheduleUpdateScale());
  let legendResizeObserver: ResizeObserver | null = null;

  const setupLegendResizeObserver = (mediaContainerEl: HTMLElement) => {
    const isManeuverWindow = mediaContainerEl.classList.contains('maneuver-window-media-container');
    if (
      !window.ResizeObserver ||
      (!isPerformanceScalingContainer(mediaContainerEl) && !isManeuverWindow)
    ) {
      return;
    }

    const attachLegendObserver = (legendSection: Element) => {
      if (legendResizeObserver) {
        legendResizeObserver.disconnect();
      }
      lastLegendHeightForScaling = Math.round((legendSection as HTMLElement).getBoundingClientRect().height);
      legendResizeObserver = new ResizeObserver(() => {
        const container = getMediaContainer?.() ?? document.getElementById('media-container');
        if (!container) return;
        const section = isManeuverWindow
          ? getManeuverWindowLegendElement(container)
          : (container.querySelector('.performance-legend-section') as HTMLElement | null);
        if (!section) return;
        const h = Math.round(section.getBoundingClientRect().height);
        if (
          lastLegendHeightForScaling >= 0 &&
          Math.abs(h - lastLegendHeightForScaling) <= LAYOUT_EPSILON_PX
        ) {
          return;
        }
        lastLegendHeightForScaling = h;
        scheduleUpdateScale();
      });
      legendResizeObserver.observe(legendSection);
    };

    const legendEl = isManeuverWindow
      ? getManeuverWindowLegendElement(mediaContainerEl)
      : mediaContainerEl.querySelector('.performance-legend-section');
    if (legendEl) {
      attachLegendObserver(legendEl);
      return;
    }

    let legendPollCount = 0;
    const pollLegend = () => {
      const container = getMediaContainer?.() ?? document.getElementById('media-container');
      if (!container) return;
      const el = isManeuverWindow
        ? getManeuverWindowLegendElement(container)
        : container.querySelector('.performance-legend-section');
      if (el) {
        attachLegendObserver(el);
      } else if (legendPollCount < 20) {
        legendPollCount++;
        setTimeout(pollLegend, 100);
      }
    };
    setTimeout(pollLegend, 100);
  };

  let splitPanelTransitionEndHandler: (() => void) | null = null;
  let splitPanelForCleanup: Element | null = null;
  setTimeout(() => {
    const mediaContainer = getMediaContainer?.() ?? document.getElementById('media-container');
    if (mediaContainer) {
      // If in split view, observe the split panel and the split-view container so we react to both divider drag and window resize
      const splitPanel = mediaContainer.closest('.split-panel');
      if (splitPanel) {
        resizeObserver.observe(splitPanel);
        // Also observe split-view-content so resizing the window in split view triggers scale update (panel % of a changing container)
        const splitViewContent = splitPanel.closest('.split-view-content');
        if (splitViewContent) {
          resizeObserver.observe(splitViewContent);
        }
        // Panel width uses CSS transition (0.2s); run updateScale when transition ends so we pick up final size after divider drag
        splitPanelTransitionEndHandler = (e: TransitionEvent) => {
          if (e.propertyName === 'width') scheduleUpdateScale();
        };
        splitPanelForCleanup = splitPanel;
        splitPanel.addEventListener('transitionend', splitPanelTransitionEndHandler);
        requestAnimationFrame(() => scheduleUpdateScale());
        setTimeout(() => scheduleUpdateScale(), 100);
        setTimeout(() => scheduleUpdateScale(), 350);
      } else {
        const resizeTarget = resolveResizeObserveTarget();
        if (resizeTarget) {
          resizeObserver.observe(resizeTarget);
        } else if (mediaContainer.parentElement) {
          resizeObserver.observe(mediaContainer.parentElement);
        }
        const mainContent = document.getElementById('main-content');
        if (
          mainContent &&
          (mediaContainer.classList.contains('race-summary-page') ||
            mediaContainer.classList.contains('prestart-page') ||
            mediaContainer.classList.contains('maneuvers-page'))
        ) {
          resizeObserver.observe(mainContent);
        }
        setupLegendResizeObserver(mediaContainer);
      }
    } else {
      // Retry setting up ResizeObserver if container not found yet
      let observerRetryCount = 0;
      const trySetupObserver = () => {
        const container = getMediaContainer?.() ?? document.getElementById('media-container');
        if (container) {
          const splitPanel = container.closest('.split-panel');
          if (splitPanel) {
            resizeObserver.observe(splitPanel);
            const splitViewContent = splitPanel.closest('.split-view-content');
            if (splitViewContent) {
              resizeObserver.observe(splitViewContent);
            }
            splitPanelTransitionEndHandler = (e: TransitionEvent) => {
              if (e.propertyName === 'width') scheduleUpdateScale();
            };
            splitPanelForCleanup = splitPanel;
            splitPanel.addEventListener('transitionend', splitPanelTransitionEndHandler);
            requestAnimationFrame(() => scheduleUpdateScale());
            setTimeout(() => scheduleUpdateScale(), 100);
            setTimeout(() => scheduleUpdateScale(), 350);
          } else {
            const resizeTarget = resolveResizeObserveTarget();
            if (resizeTarget) {
              resizeObserver.observe(resizeTarget);
            } else if (container.parentElement) {
              resizeObserver.observe(container.parentElement);
            }
            const mainContent = document.getElementById('main-content');
            if (
              mainContent &&
              (container.classList.contains('race-summary-page') ||
                container.classList.contains('prestart-page') ||
                container.classList.contains('maneuvers-page'))
            ) {
              resizeObserver.observe(mainContent);
            }
            setupLegendResizeObserver(container);
          }
        } else if (observerRetryCount < 10) {
          observerRetryCount++;
          setTimeout(trySetupObserver, 100);
        }
      };
      trySetupObserver();
    }
  }, 200);

  // Extra delayed updates for race-summary / prestart / calibration so dimensions apply after layout has settled
  setTimeout(() => {
    const container = document.getElementById('media-container');
    if (
      container?.classList.contains('race-summary-page') ||
      container?.classList.contains('prestart-page') ||
      container?.classList.contains('calibration-page') ||
      container?.classList.contains('fleet-calibration-page')
    ) {
      scheduleUpdateScale();
    }
  }, 400);
  setTimeout(() => {
    const container = document.getElementById('media-container');
    if (
      container?.classList.contains('race-summary-page') ||
      container?.classList.contains('prestart-page') ||
      container?.classList.contains('calibration-page') ||
      container?.classList.contains('fleet-calibration-page')
    ) {
      scheduleUpdateScale();
    }
  }, 800);

  setTimeout(() => {
    const container = document.getElementById('media-container');
    if (container?.classList.contains('fleet-performance-page') || container?.classList.contains('performance-page')) {
      scheduleUpdateScale();
    }
  }, 400);
  setTimeout(() => {
    const container = document.getElementById('media-container');
    if (container?.classList.contains('fleet-performance-page') || container?.classList.contains('performance-page')) {
      scheduleUpdateScale();
    }
  }, 1000);

  window.addEventListener('resize', scheduleUpdateScale);

  const onSplitPanelResize = () => scheduleUpdateScale();
  document.addEventListener(SPLIT_PANEL_RESIZE_EVENT, onSplitPanelResize);
  
  let visualViewportResizeHandler: (() => void) | null = null;
  let visualViewportScrollHandler: (() => void) | null = null;
  if (window.visualViewport) {
    visualViewportResizeHandler = () => scheduleUpdateScale();
    visualViewportScrollHandler = () => {
      const container = getMediaContainer?.() ?? document.getElementById('media-container');
      if (container && isPerformanceScalingContainer(container)) {
        return;
      }
      scheduleUpdateScale();
    };
    window.visualViewport.addEventListener('resize', visualViewportResizeHandler);
    window.visualViewport.addEventListener('scroll', visualViewportScrollHandler, { passive: true });
  }
  
  // Track last known dimensions to detect changes when moving between monitors
  let lastKnownWidth = window.innerWidth;
  let lastKnownHeight = window.innerHeight;
  
  // Handle window focus - recalculate when window regains focus (often happens when moving between monitors)
  const handleWindowFocus = () => {
    const currentWidth = window.innerWidth;
    const currentHeight = window.innerHeight;
    
    // If dimensions changed, recalculate scale
    if (currentWidth !== lastKnownWidth || currentHeight !== lastKnownHeight) {
      lastKnownWidth = currentWidth;
      lastKnownHeight = currentHeight;
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        scheduleUpdateScale();
      });
    }
  };
  window.addEventListener('focus', handleWindowFocus);
  
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      if (currentWidth !== lastKnownWidth || currentHeight !== lastKnownHeight) {
        lastKnownWidth = currentWidth;
        lastKnownHeight = currentHeight;
        requestAnimationFrame(() => {
          scheduleUpdateScale();
        });
      } else {
        requestAnimationFrame(() => {
          scheduleUpdateScale();
        });
      }
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  const checkDimensionChanges = () => {
    const currentWidth = window.innerWidth;
    const currentHeight = window.innerHeight;
    
    if (currentWidth !== lastKnownWidth || currentHeight !== lastKnownHeight) {
      lastKnownWidth = currentWidth;
      lastKnownHeight = currentHeight;
      scheduleUpdateScale();
    }
  };
  dimensionCheckInterval = window.setInterval(checkDimensionChanges, 500);

  const containerForPolling = getMediaContainer?.() ?? document.getElementById('media-container');
  if (containerForPolling && isPerformanceScalingContainer(containerForPolling)) {
    perfPollingStopTimeout = setTimeout(() => {
      if (dimensionCheckInterval !== null) {
        clearInterval(dimensionCheckInterval);
        dimensionCheckInterval = null;
      }
      perfPollingStopTimeout = null;
    }, 8000);
  }
  
  // Watch for sidebar collapse/expand changes to recalculate scale
  let sidebarMutationObserver: MutationObserver | null = null;
  const setupSidebarObserver = () => {
    const sidebar = document.querySelector('.sidebar:not(.mobile)') || document.querySelector('.sidebar');
    if (sidebar && !sidebar.classList.contains('mobile')) {
      sidebarMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            // Sidebar class changed (collapsed/expanded), recalculate scale
            requestAnimationFrame(() => {
              scheduleUpdateScale();
            });
          }
        });
      });
      sidebarMutationObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  };
  // Try to set up sidebar observer immediately, and retry if sidebar not found
  setTimeout(() => {
    setupSidebarObserver();
    // Retry a few times in case sidebar loads late
    let retryCount = 0;
    const retrySetup = () => {
      if (!sidebarMutationObserver && retryCount < 10) {
        setupSidebarObserver();
        if (!sidebarMutationObserver) {
          retryCount++;
          setTimeout(retrySetup, 200);
        }
      }
    };
    retrySetup();
  }, 100);
  
  // Return cleanup function
  return () => {
    if (splitPanelForCleanup && splitPanelTransitionEndHandler) {
      splitPanelForCleanup.removeEventListener('transitionend', splitPanelTransitionEndHandler);
      splitPanelForCleanup = null;
      splitPanelTransitionEndHandler = null;
    }
    if (updateScaleRafId != null) {
      cancelAnimationFrame(updateScaleRafId);
      updateScaleRafId = null;
    }
    if (legendResizeObserver) {
      legendResizeObserver.disconnect();
      legendResizeObserver = null;
    }
    if (reportContentObserver) {
      reportContentObserver.disconnect();
      reportContentObserver = null;
    }
    if (perfPollingStopTimeout !== null) {
      clearTimeout(perfPollingStopTimeout);
      perfPollingStopTimeout = null;
    }
    document.removeEventListener(SPLIT_PANEL_RESIZE_EVENT, onSplitPanelResize);
    resizeObserver.disconnect();
    window.removeEventListener('resize', scheduleUpdateScale);
    window.removeEventListener('focus', handleWindowFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (dimensionCheckInterval !== null) {
      clearInterval(dimensionCheckInterval);
      dimensionCheckInterval = null;
    }
    if (sidebarMutationObserver) {
      sidebarMutationObserver.disconnect();
      sidebarMutationObserver = null;
    }
    if (window.visualViewport && visualViewportResizeHandler) {
      window.visualViewport.removeEventListener('resize', visualViewportResizeHandler);
    }
    if (window.visualViewport && visualViewportScrollHandler) {
      window.visualViewport.removeEventListener('scroll', visualViewportScrollHandler);
    }
    
    // Remove scaling classes and reset layout width variable
    document.body.classList.remove('scaling-page');
    document.documentElement.classList.remove('scaling-page');
    document.documentElement.style.setProperty('--layout-base-width', '1620px');

    // Also remove scaling-initializing class from media-container if it exists
    const mediaContainer = document.getElementById('media-container');
    if (mediaContainer) {
      mediaContainer.classList.remove('scaling-initializing');
    }
    
    // Force a repaint to ensure browser updates the display immediately
    // This helps prevent visual artifacts when navigation happens quickly
    requestAnimationFrame(() => {
      // Double-check classes are removed (helps when browser is under load)
      if (document.body.classList.contains('scaling-page')) {
        document.body.classList.remove('scaling-page');
      }
      if (document.documentElement.classList.contains('scaling-page')) {
        document.documentElement.classList.remove('scaling-page');
      }
    });
  };
}

export async function deleteData(url: string, body_json: any, signal?: AbortSignal): Promise<ApiResponse> {
	try {
		// Import authManager dynamically to avoid circular dependencies
		const { authManager } = await import('./authManager');
		
		// Construct full URL
		// If URL is already absolute (starts with http), use as-is
		// If URL starts with /api, use as-is (already a relative URL for nginx)
		// Otherwise, prepend API_BASE_URL
		let fullUrl: string;
		if (url.startsWith('http')) {
			fullUrl = url;
		} else if (url.startsWith('/api')) {
			// URL already starts with /api, use as-is
			fullUrl = url;
		} else {
			// Prepend API_BASE_URL for relative URLs
			fullUrl = config.API_BASE_URL + url;
		}
		const response = await authManager.makeAuthenticatedRequest(fullUrl, {
			method: "DELETE",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body_json),
			signal: signal
		});

		// Let backend sendResponse handle logging with proper source information
		if (response.ok) {
			const response_json: ApiResponse = await response.json();
			return response_json;
		} else {
			// Try to extract error message and full response body from response
			// Clone the response so we can read it without consuming it
			const responseClone = response.clone();
			let serverErrorMessage: string | null = null;
			let serverErrorBody: any = null;
			try {
				const contentType = response.headers.get('content-type');
				if (contentType && contentType.includes('application/json')) {
					const text = await responseClone.text();
					if (text && text.trim() !== '') {
						const errorResponse = JSON.parse(text);
						// Server returns { success: false, message: "...", data: {...} } format
						// OR FastAPI HTTPException format: { detail: "..." }
						serverErrorMessage = errorResponse.message || errorResponse.error || errorResponse.detail || null;
						
						// For script execution errors, try to extract more detailed error message from data.error_lines
						if (errorResponse.data?.error_lines && Array.isArray(errorResponse.data.error_lines) && errorResponse.data.error_lines.length > 0) {
							const lastError = errorResponse.data.error_lines[errorResponse.data.error_lines.length - 1];
							if (lastError && typeof lastError === 'string' && lastError.trim()) {
								// Use the last error line as the message if it's more specific than the generic message
								if (!serverErrorMessage || serverErrorMessage === 'Script execution failed' || serverErrorMessage === 'Server error') {
									serverErrorMessage = lastError.length > 300 ? lastError.substring(0, 300) + '...' : lastError;
								}
							}
						}
						
						// Capture the full error response body (includes data with error_lines, output_lines, etc.)
						serverErrorBody = errorResponse;
						
						// Log for debugging. Use summary only — never full errorResponse (avoids huge DB payloads).
						const errorSummaryDel = buildErrorResponseLogSummary(errorResponse, response.status, serverErrorMessage, fullUrl, 'DELETE');
						const summaryStrDel = JSON.stringify(errorSummaryDel);
						logError('[global.ts] Server error response captured:', summaryStrDel.length > ERROR_LOG_CONTEXT_MAX_LENGTH ? summaryStrDel.substring(0, ERROR_LOG_CONTEXT_MAX_LENGTH) + '...' : summaryStrDel);
					} else {
						logError('[global.ts] Server returned empty error response body');
					}
				} else {
					const plainBody = (await responseClone.text()).trim();
					const { logMessage, userMessage } = summarizeNonJsonErrorResponse(
						response.status,
						contentType,
						plainBody
					);
					serverErrorMessage = userMessage;
					logError(logMessage);
				}
			} catch (parseError) {
				// If we can't parse the error, log it for debugging
				logError('[global.ts] Error parsing server error response:', parseError);
			}

			const streamSoftFailDel = await tryStreamSoftFailResponse(
				fullUrl,
				response,
				'deleteData',
				serverErrorMessage
			);
			if (streamSoftFailDel) {
				return streamSoftFailDel;
			}
			
			// Handle specific HTTP errors consistently
			let error: AppError;
			switch (response.status) {
				case 401: {
					try {
						const { authManager } = await import('./authManager');
						const authResult = await handleUnauthorizedWithResilientRefresh({
							fullUrl,
							retryRequest: () =>
								authManager.makeAuthenticatedRequest(fullUrl, {
									method: 'DELETE',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify(body_json),
									signal,
								}),
						});

						if (authResult.recovered && authResult.response) {
							const response_json: ApiResponse = await authResult.response.json();
							return response_json;
						}

						if (authResult.abandonSession) {
							await abandonUserSession(fullUrl);
						}
					} catch (redirectErr) {
						logError('Failed to redirect to login:', redirectErr);
					}

					error = new AuthError('Authentication required');
					break;
				}
				case 404:
					error = new NotFoundError('API endpoint');
					break;
				case 422:
					error = new ValidationError('Invalid request data');
					break;
				case 500:
					error = new AppError('Server error', 500);
					break;
				case 502:
				case 503:
				case 504: {
					const msg = serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`;
					error = new AppError(msg, response.status);
					break;
				}
				default:
					error = new AppError(serverErrorMessage || `HTTP ${response.status}: ${response.statusText}`, response.status);
			}
			
			// Pass both the request body and the server's error response body
			return handleError(error, 'global.ts', { 
				url: fullUrl, 
				status: response.status, 
				body: body_json, // Request body
				errorResponse: serverErrorBody // Server's error response (includes data with error_lines, output_lines, etc.)
			});
		}
	} catch (error) {
		if ((error as Error).name === 'AbortError') {
			return { success: false, message: 'Request cancelled', status: 0, type: 'AbortError' };
		}
		
		const networkError = new NetworkError(`Network error: ${(error as Error).message}`);
		return handleError(networkError, 'global.ts', { url, body: body_json, signal: !!signal });
	}
}
